import config from "../../core/config";
import { getActionItemCategory } from "./actionItemCategories";
import {
  EXTENSION_OPENED_QUERY_PARAM,
  EXTENSION_OPENED_QUERY_VALUE,
} from "../../core/utils/extensionSession";

export {
  EXTENSION_OPENED_QUERY_PARAM,
  EXTENSION_OPENED_QUERY_VALUE,
  EXTENSION_OPENED_SESSION_STORAGE_KEY,
  WEBAPP_CLOSE_EXTENSION_TAB_MESSAGE,
  WEBAPP_CLOSE_EXTENSION_TAB_SOURCE,
  isExtensionOpenedTab,
  isExtensionOpenedTabPersisted,
  persistExtensionOpenedTab,
  shouldCloseExtensionOpenedTabAfterLogin,
  tryCloseExtensionOpenedTab,
} from "../../core/utils/extensionSession";

const MANUAL_FILE_ID_PREFIX = "manual::";

/** Synthetic workflow id for items created via the capture API (no linked workflow). */
export const CAPTURE_WORKFLOW_ID = "__capture__";

/** Display name for {@link CAPTURE_WORKFLOW_ID} rows in list UI and filters. */
export const CAPTURE_WORKFLOW_NAME = "Captured";

/** Toolbar filter value for user-added rows (`file_id` starts with `manual::`), not a real workflow id. */
export const MANUAL_WORKFLOW_FILTER_ID = "__manual__";

/**
 * Row-click multi-select and bulk bar (Done / Archive). Off while product is on hold —
 * single-click selection re-renders rows and breaks double-click title edit.
 */
export const ENABLE_ACTION_ITEMS_BULK_SELECT = false;

/**
 * True when the action item was captured without a workflow match.
 *
 * @example
 * isCaptureWorkflowItem({ workflow_id: "__capture__" });
 * // true
 *
 * @example
 * isCaptureWorkflowItem({ workflow_id: "2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1" });
 * // false
 */
export const isCaptureWorkflowItem = (item) =>
  String(item?.workflow_id || "").trim() === CAPTURE_WORKFLOW_ID;
const SOURCE_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SOURCE_WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * True when the action item was created via the manual add flow (`file_id` starts with `manual::`).
 *
 * @example
 * isManualActionItem({ file_id: "manual::3a7f2b1c-..." });
 * // true
 *
 * @example
 * isManualActionItem({ file_id: "msg-123" });
 * // false
 */
export const isManualActionItem = (item) =>
  String(item?.file_id || "").startsWith(MANUAL_FILE_ID_PREFIX);

const PENDING_MANUAL_FILE_ID_PREFIX = `${MANUAL_FILE_ID_PREFIX}pending-`;

/**
 * True while a manual add is optimistic (awaiting server refresh).
 *
 * @example
 * isPendingManualActionItem({ file_id: "manual::pending-1717000000123" });
 * // true
 */
export const isPendingManualActionItem = (item) =>
  String(item?.file_id || "").startsWith(PENDING_MANUAL_FILE_ID_PREFIX);

/**
 * Parse `meeting_from_label` stored as UTC clock time into epoch ms for comparisons.
 *
 * @example
 * parseMeetingLabel("13-04-2026 03:51 AM");
 * // 1712981460000 (depends on label format)
 */
export const parseMeetingLabel = (label) => {
  if (!label) return 0;
  const m = label.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i);
  if (!m) return 0;
  const [, dd, mm, yyyy, hh, min, ampm] = m;
  let hours = parseInt(hh, 10);
  if (ampm.toUpperCase() === "PM" && hours !== 12) hours += 12;
  if (ampm.toUpperCase() === "AM" && hours === 12) hours = 0;
  return Date.UTC(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10), hours, parseInt(min, 10));
};

/**
 * Format epoch ms as a `meeting_from_label` UTC clock-time string (inverse of
 * `parseMeetingLabel`), so a raw occurrence timestamp can drive the same
 * "Show all from this meeting" filter used elsewhere in the action items list.
 *
 * @example
 * formatEpochMsAsMeetingLabel(1712981460000);
 * // "13-04-2026 03:51 AM"
 */
export const formatEpochMsAsMeetingLabel = (epochMs) => {
  const ms = Number(epochMs) || 0;
  if (ms <= 0) return "";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hh = String(hours).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${minutes} ${ampm}`;
};

/**
 * Format epoch ms for the action-items Source column (minute precision, browser local time).
 *
 * @example
 * formatMinuteTimestampForSourceColumn(Date.parse("2026-05-19T07:59:00Z"));
 * // "Tue, May 19 · 1:29 PM" when local year is 2026 and browser is Asia/Kolkata
 *
 * @example
 * formatMinuteTimestampForSourceColumn(Date.parse("2025-05-19T07:59:00Z"));
 * // "Mon, May 19, 2025 · 1:29 PM" when browser is Asia/Kolkata
 */
const formatMinuteTimestampForSourceColumn = (epochMs) => {
  if (!epochMs) return "";
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return "";

  const weekday = SOURCE_WEEKDAY_NAMES[date.getDay()];
  const month = SOURCE_MONTH_NAMES[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const period = date.getHours() >= 12 ? "PM" : "AM";
  const hour = date.getHours() % 12 || 12;
  const dateLabel = year === currentYear
    ? `${weekday}, ${month} ${day}`
    : `${weekday}, ${month} ${day}, ${year}`;

  return `${dateLabel} · ${hour}:${minutes} ${period}`;
};

/**
 * Converts backend `meeting_from_label` (UTC clock time) into local-time display text.
 *
 * @example
 * formatMeetingLabelForDisplay("13-04-2026 03:51 AM");
 * // "Mon, Apr 13 · 9:21 AM" when local year is 2026 and browser is Asia/Kolkata
 */
export const formatMeetingLabelForDisplay = (label) => {
  const utcMs = parseMeetingLabel(label);
  if (!utcMs) return "";
  return formatMinuteTimestampForSourceColumn(utcMs);
};

/**
 * Format manual action-item `created_at` for the Source column (browser local time).
 *
 * @example
 * formatActionItemCreatedAtForDisplay("2026-04-12T15:47:00+00:00");
 * // "Sun, Apr 12 · 11:47 AM" when local year is 2026 and browser is US Eastern
 */
export const formatActionItemCreatedAtForDisplay = (createdAt) => {
  const raw = String(createdAt || "").trim();
  if (!raw) return "";
  const epochMs = Date.parse(raw);
  if (Number.isNaN(epochMs)) return "";
  return formatMinuteTimestampForSourceColumn(epochMs);
};

const formatDateShort = (epochMs) => {
  if (!epochMs) return "";
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return "";
  const month = SOURCE_MONTH_NAMES[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();
  return year === currentYear ? `${month} ${day}` : `${month} ${day}, ${year}`;
};

export const formatMeetingLabelShort = (label) => {
  const utcMs = parseMeetingLabel(label);
  if (!utcMs) return "";
  return formatDateShort(utcMs);
};

export const formatActionItemCreatedAtShort = (createdAt) => {
  const raw = String(createdAt || "").trim();
  if (!raw) return "";
  const epochMs = Date.parse(raw);
  if (Number.isNaN(epochMs)) return "";
  return formatDateShort(epochMs);
};

/**
 * Parse action-item `created_at` into epoch ms for sorting and display.
 *
 * @example
 * parseActionItemCreatedAtMs({ created_at: "2026-05-13T09:00:00+00:00" });
 * // 1747126800000 (depends on string)
 */
export const parseActionItemCreatedAtMs = (item) => {
  const parsed = Date.parse(String(item?.created_at || "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Pick the source message whose sent time is closest to the meeting label timestamp.
 *
 * @example
 * findClosestMessage([{ sent_time: 1712981400000 }], "13-04-2026 03:51 AM");
 * // the message object or null
 */
export const findClosestMessage = (messages, meetingFromLabel) => {
  const target = parseMeetingLabel(meetingFromLabel);
  if (!target || !messages.length) return null;
  let best = null;
  let bestDiff = Infinity;
  messages.forEach((msg) => {
    const raw = msg.sent_time;
    if (!raw) return;
    const ts = typeof raw === "number" ? raw : new Date(raw).getTime();
    if (isNaN(ts)) return;
    const diff = Math.abs(ts - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = msg;
    }
  });
  return best;
};

/**
 * Resolve a source message for citations using file id first, then time proximity.
 *
 * @example
 * findMessageForDeepLink(msgs, "file-1", "");
 * // message with matching message_id or file_id
 */
export const findMessageForDeepLink = (messages, fileId, meetingFromLabel) => {
  const normalizedFileId = String(fileId || "").trim();
  if (normalizedFileId) {
    const directByMessageId = messages.find(
      (msg) => String(msg?.message_id || "").trim() === normalizedFileId
    );
    if (directByMessageId) return directByMessageId;
    const directByFileId = messages.find(
      (msg) => String(msg?.file_id || "").trim() === normalizedFileId
    );
    if (directByFileId) return directByFileId;
  }
  return findClosestMessage(messages, meetingFromLabel);
};

/**
 * Match a deep-linked query to one row in the current list.
 *
 * @example
 * resolveDeepLinkItem(items, "Send deck", "05-05-2026 11:00 AM", "msg-1");
 * // the matching item or null
 */
export const resolveDeepLinkItem = (items, actionItemText, meetingFromLabel, fileId) => {
  const normalizedActionText = String(actionItemText || "").trim().toLowerCase();
  const normalizedMeetingLabel = String(meetingFromLabel || "").trim();
  const normalizedFileId = String(fileId || "").trim();
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  if (normalizedFileId) {
    const byFileId = items.find(
      (item) => String(item?.file_id || "").trim() === normalizedFileId
    );
    if (byFileId) {
      return byFileId;
    }
  }
  if (normalizedActionText) {
    const exactMatch = items.find(
      (item) =>
        String(item?.action_item || "").trim().toLowerCase() === normalizedActionText
    );
    if (exactMatch) {
      return exactMatch;
    }
  }
  if (normalizedMeetingLabel) {
    const byMeetingLabel = items.find(
      (item) => String(item?.meeting_from_label || "").trim() === normalizedMeetingLabel
    );
    if (byMeetingLabel) {
      return byMeetingLabel;
    }
  }
  return null;
};

/**
 * True when a queued "open original source" load belongs to the task that just opened.
 *
 * Used so citation loading waits until after the details sidebar resets source state
 * for the new item — otherwise that reset wipes the panel and the tab only shows the task.
 *
 * @param {{ item?: Object, sourceSnippet?: string } | null | undefined} pending
 * @param {string | null | undefined} nextDetailId - `getItemStableId` of the open task.
 * @returns {boolean}
 * @example
 * pendingSourceLoadMatchesDetail(
 *   { item: { workflow_id: "wf-1", key: "k1", file_id: "msg-1" } },
 *   "wf-1::k1::msg-1"
 * );
 * // true
 * @example
 * pendingSourceLoadMatchesDetail(
 *   { item: { workflow_id: "wf-1", key: "k1", file_id: "msg-1" } },
 *   "wf-other::k2::msg-2"
 * );
 * // false
 */
export const pendingSourceLoadMatchesDetail = (pending, nextDetailId) => {
  const item = pending?.item;
  const detailId = String(nextDetailId || "").trim();
  if (!item || !detailId) return false;
  return getItemStableId(item) === detailId;
};

/**
 * Build a compact action-item title for list/card rows while preserving full text elsewhere.
 * The title is derived from the first actionable clause instead of character trimming.
 *
 * @example
 * buildCompactActionItemTitle("Please send the revised pricing sheet before Friday and include legal terms.");
 * // "Send the revised pricing sheet"
 *
 * @example
 * buildCompactActionItemTitle("");
 * // "View"
 */
/** Separator between compact title and captured selection body in `action_item` text. */
export const ACTION_ITEM_CONTEXT_MARKER = "\n\nContext from selection:\n";

/** Trailing line in `action_item` when the list API omits `page_url`. */
const ACTION_ITEM_SOURCE_PAGE_LINE_RE = /\n\nSource page:\s*(.+)\s*$/s;

/**
 * Remove embedded source-page URL from text shown in the UI (URL stays on `page_url`).
 *
 * @example
 * stripSourcePageLineForDisplay("Follow up\n\nSource page: https://app.slack.com/client/T1");
 * // "Follow up"
 *
 * @example
 * stripSourcePageLineForDisplay("No source line");
 * // "No source line"
 */
export const stripSourcePageLineForDisplay = (actionItemText) => {
  const text = String(actionItemText || "");
  if (!text) return text;
  return text.replace(ACTION_ITEM_SOURCE_PAGE_LINE_RE, "").trimEnd();
};

/**
 * Resolve the original page URL for a manual / highlight capture row.
 *
 * @example
 * resolveActionItemPageUrl({ page_url: "https://mail.google.com/mail/u/0/" });
 * // "https://mail.google.com/mail/u/0/"
 *
 * @example
 * resolveActionItemPageUrl({
 *   action_item: "Send deck\n\nSource page: https://app.slack.com/client/T1",
 * });
 * // "https://app.slack.com/client/T1"
 */
export const resolveActionItemPageUrl = (item) => {
  const direct = String(item?.page_url || "").trim();
  if (direct) return direct;
  const actionItem = String(item?.action_item || "").trim();
  const match = actionItem.match(ACTION_ITEM_SOURCE_PAGE_LINE_RE);
  return match?.[1] ? String(match[1]).trim() : "";
};

/** Granola document URL path used for web notes with chat + transcript panel. */
export const GRANOLA_CHAT_DOCUMENT_PREFIX = "https://notes.granola.ai/d/";

/** Query string Granola uses to open the chat-transcript panel in the web note. */
export const GRANOLA_CHAT_URL_QUERY =
  "utm_source=granola-app&utm_medium=embedded&utm_campaign=document-share&utm_content=chat-transcript-panel";

const GRANOLA_MEETING_PATH_RE = /^\/(?:t|d)\/([^/]+)/i;

/**
 * Build a Granola web note URL that opens chat with the meeting transcript.
 *
 * Notes often show `/t/{id}` in copy text, but the working browser URL is
 * `/d/{id}` with UTM params (see Granola's "Chat with meeting transcript" link).
 *
 * @example
 * buildGranolaChatUrl("48f45460-9d92-4d7a-b8d9-fe7883c1327a");
 * // "https://notes.granola.ai/d/48f45460-...?...&utm_content=chat-transcript-panel"
 */
export const buildGranolaChatUrl = (meetingId = "") => {
  const id = String(meetingId || "").trim();
  if (!id) return "";
  return `${GRANOLA_CHAT_DOCUMENT_PREFIX}${id}?${GRANOLA_CHAT_URL_QUERY}`;
};

/**
 * Parse a meeting id from a Granola notes URL (`/t/` or `/d/` path).
 *
 * @example
 * extractGranolaMeetingIdFromUrl("https://notes.granola.ai/t/abc-123");
 * // "abc-123"
 */
export const extractGranolaMeetingIdFromUrl = (url = "") => {
  const text = String(url || "").trim();
  if (!text || !isGranolaNotesUrl(text)) return "";
  try {
    const match = String(new URL(text).pathname || "").match(GRANOLA_MEETING_PATH_RE);
    return match?.[1] ? decodeURIComponent(match[1]) : "";
  } catch (_) {
    return "";
  }
};

/**
 * Normalize `/t/` or bare `/d/` Granola links to the chat-transcript document URL.
 *
 * @example
 * normalizeGranolaChatUrl("https://notes.granola.ai/t/48f45460-9d92-4d7a-b8d9-fe7883c1327a");
 * // "https://notes.granola.ai/d/48f45460-...?...&utm_content=chat-transcript-panel"
 */
export const normalizeGranolaChatUrl = (url = "") => {
  const meetingId = extractGranolaMeetingIdFromUrl(url);
  return meetingId ? buildGranolaChatUrl(meetingId) : String(url || "").trim();
};

/**
 * Return true when a URL points at Granola's notes chat host.
 *
 * @example
 * isGranolaNotesUrl("https://notes.granola.ai/t/abc");
 * // true
 */
export const isGranolaNotesUrl = (url = "") => {
  const text = String(url || "").trim();
  if (!text) return false;
  try {
    const host = new URL(text).hostname.replace(/^www\./i, "");
    return host === "notes.granola.ai";
  } catch (_) {
    return false;
  }
};

/**
 * Return true when an action item came from a Granola daily-digest workflow.
 *
 * @example
 * isGranolaConnectorItem(
 *   { workflow_id: "wf-1" },
 *   { workflowTypeById: new Map([["wf-1", "granola_daily_digest"]]) }
 * );
 * // true
 */
export const isGranolaConnectorItem = (item, { workflowTypeById } = {}) => {
  if (!item || typeof item !== "object") return false;
  const workflowId = String(item.workflow_id || "").trim();
  const workflowType =
    String(item.workflow_type || "").trim() ||
    String(workflowTypeById?.get(workflowId) || "").trim();
  if (workflowType === "granola_daily_digest") return true;
  const workflowName = String(item.workflow_name || "").trim().toLowerCase();
  return workflowName.includes("granola");
};

/**
 * Return true when an action item came from a Notion page workflow.
 *
 * @example
 * isNotionConnectorItem(
 *   { workflow_id: "wf-1" },
 *   { workflowTypeById: new Map([["wf-1", "notion_daily_digest"]]) }
 * );
 * // true
 */
export const isNotionConnectorItem = (item, { workflowTypeById } = {}) => {
  if (!item || typeof item !== "object") return false;
  const workflowId = String(item.workflow_id || "").trim();
  const workflowType =
    String(item.workflow_type || "").trim() ||
    String(workflowTypeById?.get(workflowId) || "").trim();
  return workflowType === "notion_daily_digest";
};

/**
 * Link to the Notion page a task came from (page-level; Notion exposes no block anchor).
 *
 * @example
 * resolveNotionPageUrl(
 *   { workflow_id: "wf-1", page_url: "https://www.notion.so/Weekly-9f1c" },
 *   { workflowTypeById: new Map([["wf-1", "notion_daily_digest"]]) }
 * );
 * // "https://www.notion.so/Weekly-9f1c"
 */
export const resolveNotionPageUrl = (item, { workflowTypeById } = {}) =>
  isNotionConnectorItem(item, { workflowTypeById }) ? resolveActionItemPageUrl(item) : "";

/**
 * Who a document source (Notion) assigns a task to, for the task details.
 *
 * Empty for items from other sources, which carry no `ownership`.
 *
 * @example
 * resolveActionItemAssigneeLabel({ ownership: "team", assignee_text: "Priya" });
 * // "Priya"
 *
 * @example
 * resolveActionItemAssigneeLabel({ ownership: "unassigned" });
 * // "Not assigned on the page"
 */
export const resolveActionItemAssigneeLabel = (item) => {
  const ownership = String(item?.ownership || "").trim();
  if (ownership === "mine") return "You";
  if (ownership === "team") return String(item?.assignee_text || "").trim() || "Someone else";
  if (ownership === "unassigned") return "Not assigned on the page";
  return "";
};

/**
 * Resolve a Granola chat URL for connector-sourced tasks.
 *
 * Prefers an existing notes.granola.ai page_url; otherwise builds from meeting_id.
 *
 * @example
 * resolveGranolaChatUrl({ page_url: "https://notes.granola.ai/t/abc" });
 * // "https://notes.granola.ai/d/abc?...&utm_content=chat-transcript-panel"
 *
 * @example
 * resolveGranolaChatUrl(
 *   { workflow_id: "wf-1", meeting_id: "abc" },
 *   { workflowTypeById: new Map([["wf-1", "granola_daily_digest"]]) }
 * );
 * // "https://notes.granola.ai/d/abc?...&utm_content=chat-transcript-panel"
 */
export const resolveGranolaChatUrl = (item, { workflowTypeById } = {}) => {
  if (!item || typeof item !== "object") return "";
  if (!isGranolaConnectorItem(item, { workflowTypeById })) return "";
  const pageUrl = resolveActionItemPageUrl(item);
  if (pageUrl) {
    return isGranolaNotesUrl(pageUrl) ? normalizeGranolaChatUrl(pageUrl) : "";
  }
  return buildGranolaChatUrl(item.meeting_id);
};

/**
 * Append a recoverable source-page line when creating captures (list API may omit `page_url`).
 *
 * @example
 * appendSourcePageLineIfMissing("Follow up on pricing", "https://zoom.us/j/123");
 * // "Follow up on pricing\n\nSource page: https://zoom.us/j/123"
 *
 * @example
 * appendSourcePageLineIfMissing("Already\n\nSource page: https://a.com", "https://a.com");
 * // "Already\n\nSource page: https://a.com"
 */
export const appendSourcePageLineIfMissing = (actionItemText, pageUrl = "") => {
  const text = String(actionItemText || "").trim();
  const url = String(pageUrl || "").trim();
  if (!text || !url) return text;
  if (ACTION_ITEM_SOURCE_PAGE_LINE_RE.test(text) || text.includes(url)) return text;
  return `${text}\n\nSource page: ${url}`;
};

/**
 * Merge a resolved `page_url` onto API rows when only embedded in `action_item` text.
 *
 * @example
 * enrichActionItemRow({ action_item: "Task\n\nSource page: https://x.com", file_id: "manual::1" });
 * // { action_item: "...", file_id: "manual::1", page_url: "https://x.com" }
 */
export const enrichActionItemRow = (item) => {
  if (!item || typeof item !== "object") return item;
  const page_url = resolveActionItemPageUrl(item);
  const enriched = page_url ? { ...item, page_url } : { ...item };
  if (item.item_id !== undefined && item.item_id !== null) {
    enriched.item_id = String(item.item_id);
  }
  return enriched;
};

/**
 * Capitalize the first letter of note/task body text for display.
 *
 * @example
 * capitalizeLeadingLetter("send the revised contract by Friday");
 * // "Send the revised contract by Friday"
 *
 * @example
 * capitalizeLeadingLetter("  follow up with legal");
 * // "  Follow up with legal"
 */
export const capitalizeLeadingLetter = (text) => {
  const value = String(text || "");
  if (!value) return value;
  const leadingWhitespace = value.match(/^\s*/)?.[0] || "";
  const rest = value.slice(leadingWhitespace.length);
  if (!rest) return value;
  return `${leadingWhitespace}${rest.charAt(0).toUpperCase()}${rest.slice(1)}`;
};

/**
 * Resolve list title vs full task body for action-item rows.
 *
 * @example
 * resolveActionItemTexts({
 *   action_title: "Review the updated call flow",
 *   action_item: "Check the new intake summary, call script preview, and follow-up outcome flow…",
 * });
 * // { displayTitle: "Review the updated call flow", fullActionText: "Check the new…", hasExpandableFullTask: true }
 *
 * @example
 * resolveActionItemTexts({
 *   action_title: "Send deck",
 *   action_item: "Send deck",
 *   source_snippet: "Please attach the latest deck PDF before EOD",
 * });
 * // fullActionText includes the selection context block
 */
export const resolveActionItemTexts = (item) => {
  const storedTitle = String(item?.action_title || "").trim();
  const actionItem = String(item?.action_item || "").trim();
  const sourceSnippet = String(item?.source_snippet || "").trim();

  let displayTitle = storedTitle || buildCompactActionItemTitle(actionItem);
  let fullActionText = actionItem;

  if (actionItem.includes(ACTION_ITEM_CONTEXT_MARKER)) {
    const header = actionItem.split(ACTION_ITEM_CONTEXT_MARKER)[0].trim();
    if (header) displayTitle = storedTitle || header;
    fullActionText = actionItem;
  } else if (storedTitle && actionItem && actionItem !== storedTitle) {
    displayTitle = storedTitle;
    fullActionText = actionItem;
  } else if (storedTitle && (!actionItem || actionItem === storedTitle) && sourceSnippet) {
    displayTitle = storedTitle;
    fullActionText = `${storedTitle}${ACTION_ITEM_CONTEXT_MARKER}${sourceSnippet}`;
  } else if (!storedTitle && actionItem) {
    displayTitle = buildCompactActionItemTitle(actionItem);
    fullActionText = actionItem;
  }

  displayTitle = displayTitle || "View";
  fullActionText = capitalizeLeadingLetter(
    stripSourcePageLineForDisplay(fullActionText || displayTitle)
  );

  const hasExpandableFullTask = Boolean(
    displayTitle && fullActionText && displayTitle !== fullActionText
  );

  return { displayTitle, fullActionText, hasExpandableFullTask };
};

export const buildCompactActionItemTitle = (actionItemText) => {
  const normalizedText = String(actionItemText || "").replace(/\s+/g, " ").trim();
  if (!normalizedText) return "View";

  // A raw verbatim-fallback paste (or a stage-1 candidate) sometimes carries a
  // leaked "Them:"/"Me:"/"Name:" speaker label -- untreated, that label survives
  // into the truncated title as a nonsensical fragment (mirrors the same fix in
  // starmesh_ai's derive_compact_title_from_action_item / strip_speaker_prefix).
  const withoutSpeakerLabel = normalizedText.replace(/^[a-z][a-z'-]*\s*:\s*/i, "");

  const withoutPrefix = withoutSpeakerLabel
    .replace(/^(please|kindly)\s+/i, "")
    .replace(/^(can|could|would)\s+you\s+/i, "")
    .replace(/^(need|remember)\s+to\s+/i, "");
  const firstSentence = withoutPrefix.split(/[.!?]/)[0].trim() || withoutPrefix;
  const primaryClause = firstSentence
    .split(/\b(?:before|after|because|so that|if|when|while|once|unless)\b/i)[0]
    .trim();
  const resolvedTitle = (primaryClause || firstSentence || withoutPrefix).replace(/\s+/g, " ").trim();
  if (!resolvedTitle) return "View";

  const words = resolvedTitle.split(" ");
  const maxWords = 8;
  if (words.length <= maxWords) return resolvedTitle;
  return `${words.slice(0, maxWords).join(" ")}…`;
};

/**
 * Build action_item + action_title from page selection when the generate API is unavailable.
 *
 * @example
 * buildLocalActionItemFromSelection("Please send the revised contract by Friday", "https://mail.google.com/");
 * // { action_title: "Send the revised contract", action_item_text: "Please send the revised contract by Friday" }
 *
 * @example
 * buildLocalActionItemFromSelection("A very long email paragraph ...", "https://zoom.us/");
 * // { action_title: "...", action_item_text: "<title>\n\nContext from selection:\n<long text>\n\nSource page: ..." }
 */
/**
 * Build a webapp URL that opens the global action-items page with citation sidebar
 * for one digest-sourced row (same query params as GlobalActionItemsPage deep link).
 *
 * @example
 * buildActionItemSourceDeepLink(
 *   { workflow_id: "wf-1", file_id: "msg-1", action_item: "Send deck" },
 *   { baseUrl: "https://app.taskmantis.com" }
 * );
 * // "https://app.taskmantis.com/business/action-items?workflow=wf-1&file_id=msg-1&action_item=Send+deck"
 *
 * @example
 * buildActionItemSourceDeepLink(
 *   { workflow_id: "__capture__", file_id: "manual::abc", source_snippet: "Follow up" },
 *   { baseUrl: "https://app.taskmantis.com" }
 * );
 * // "https://app.taskmantis.com/business/action-items?workflow=__capture__&file_id=manual::abc&..."
 */
export const buildActionItemSourceDeepLink = (item, { baseUrl = "" } = {}) => {
  const workflowId = String(item?.workflow_id || "").trim();
  const fileId = String(item?.file_id || "").trim();
  if (!fileId) return null;
  const resolvedWorkflowId =
    workflowId || (isManualActionItem(item) ? CAPTURE_WORKFLOW_ID : "");
  if (!resolvedWorkflowId) return null;

  const normalizedBase = String(baseUrl || "").trim().replace(/\/$/, "");
  if (!normalizedBase) return null;

  const url = new URL(`${normalizedBase}/business/action-items`);
  url.searchParams.set("workflow", resolvedWorkflowId);
  url.searchParams.set("file_id", fileId);

  const actionItem = String(item?.action_item || "").trim();
  if (actionItem) url.searchParams.set("action_item", actionItem);

  const meetingFromLabel = String(item?.meeting_from_label || "").trim();
  if (meetingFromLabel) url.searchParams.set("meeting_from_label", meetingFromLabel);

  const sourceSnippet = String(item?.source_snippet || "").trim();
  if (sourceSnippet) url.searchParams.set("source_snippet", sourceSnippet);

  if (isManualActionItem(item)) {
    url.searchParams.set("ext", "1");
  }

  return url.toString();
};

/** localStorage key: workflow id to select after digest setup (shared across tabs). */
export const POST_SETUP_WORKFLOW_FOCUS_KEY = "starmeshPostSetupWorkflowFocus";
const POST_SETUP_WORKFLOW_FOCUS_TTL_MS = 5 * 60 * 1000;

/**
 * Remember which connector was just set up so the opener tab can select it.
 *
 * @example
 * persistPostSetupWorkflowFocus("629b15c8-9b47-421c-b5d1-e0ceb51c6223");
 * // localStorage holds { workflowId, at }
 */
export const persistPostSetupWorkflowFocus = (workflowId) => {
  const id = String(workflowId || "").trim();
  if (!id || typeof window === "undefined") return;
  window.localStorage.setItem(
    POST_SETUP_WORKFLOW_FOCUS_KEY,
    JSON.stringify({ workflowId: id, at: Date.now() })
  );
};

/**
 * Read and clear the pending post-setup workflow focus.
 *
 * @example
 * consumePostSetupWorkflowFocus();
 * // "629b15c8-9b47-421c-b5d1-e0ceb51c6223" or ""
 */
export const consumePostSetupWorkflowFocus = () => {
  if (typeof window === "undefined") return "";
  const raw = window.localStorage.getItem(POST_SETUP_WORKFLOW_FOCUS_KEY);
  window.localStorage.removeItem(POST_SETUP_WORKFLOW_FOCUS_KEY);
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    const at = Number(parsed?.at || 0);
    if (!at || Date.now() - at > POST_SETUP_WORKFLOW_FOCUS_TTL_MS) {
      return "";
    }
    return String(parsed?.workflowId || "").trim();
  } catch {
    return "";
  }
};

/** Query param that opens the AI assist settings drawer on the global action-items page. */
export const ACTION_ITEMS_AI_SETTINGS_QUERY_PARAM = "aiSettings";

/** Business webapp route for unified action-items settings (full page). */
export const ACTION_ITEMS_SETTINGS_PATH = "/business/settings";

/** Global action-items list route (sidebar source nav is populated here). */
export const ACTION_ITEMS_LIST_PATH = "/business/action-items";

/** Upper bound for pasted transcript text in the add-task field. */
export const TRANSCRIPT_PASTE_MAX_CHARS = 150000;

/** Above this length, manual add text is summarized instead of stored verbatim. */
export const MANUAL_ACTION_ITEM_CHAR_THRESHOLD = 250;

/** localStorage: user chose manual action items instead of connect-sources onboarding. */
export const MANUAL_ACTION_ITEMS_MODE_KEY = "action_items_manual_mode_preferred";

/**
 * Whether the user skipped connect-sources onboarding for an empty account.
 *
 * @example
 * readManualActionItemsModePreferred(); // false when key unset
 */
export const readManualActionItemsModePreferred = () =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(MANUAL_ACTION_ITEMS_MODE_KEY) === "1";

/**
 * Persist manual action-items mode (empty list with add form, no connect onboarding).
 *
 * @example
 * persistManualActionItemsModePreferred();
 * readManualActionItemsModePreferred(); // true
 */
export const persistManualActionItemsModePreferred = () => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MANUAL_ACTION_ITEMS_MODE_KEY, "1");
};

/**
 * True when pathname is the global action-items home (not settings or workflow setup).
 *
 * @example
 * isBusinessActionItemsHomePath("/business/action-items"); // true
 * isBusinessActionItemsHomePath("/business/workflows/new/zoom-digest"); // false
 */
export const isBusinessActionItemsHomePath = (pathname) => {
  const normalized = String(pathname || "").replace(/\/+$/, "") || "/";
  return normalized === ACTION_ITEMS_LIST_PATH;
};

/** sessionStorage: connector setup launched from full-screen onboarding. */
export const BUSINESS_ONBOARDING_CONNECT_SETUP_SESSION_KEY =
  "business_onboarding_connect_setup";

/**
 * Mark connector setup as part of first-time onboarding (survives OAuth reloads).
 *
 * @example
 * persistBusinessOnboardingConnectSetup();
 * readBusinessOnboardingConnectSetup(); // true
 */
export const persistBusinessOnboardingConnectSetup = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(BUSINESS_ONBOARDING_CONNECT_SETUP_SESSION_KEY, "1");
};

/**
 * Whether connector setup is running inside onboarding.
 *
 * @example
 * readBusinessOnboardingConnectSetup(); // false when unset
 */
export const readBusinessOnboardingConnectSetup = () =>
  typeof window !== "undefined" &&
  window.sessionStorage.getItem(BUSINESS_ONBOARDING_CONNECT_SETUP_SESSION_KEY) === "1";

/**
 * Clear onboarding connector-setup mode after leaving the connect flow.
 *
 * @example
 * clearBusinessOnboardingConnectSetup();
 * readBusinessOnboardingConnectSetup(); // false
 */
export const clearBusinessOnboardingConnectSetup = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(BUSINESS_ONBOARDING_CONNECT_SETUP_SESSION_KEY);
};

/**
 * True for new workflow connector setup routes.
 *
 * @example
 * isBusinessConnectorSetupPath("/business/workflows/new/email-digest"); // true
 */
export const isBusinessConnectorSetupPath = (pathname) => {
  const normalized = String(pathname || "").replace(/\/+$/, "") || "/";
  return normalized.startsWith("/business/workflows/new/");
};

/**
 * Whether the business shell should hide its sidebar for connector setup flows.
 *
 * @example
 * shouldHideBusinessSidebarForOnboardingSetup("/business/workflows/new/email-digest"); // true
 * shouldHideBusinessSidebarForOnboardingSetup("/business/action-items"); // false
 */
export const shouldHideBusinessSidebarForOnboardingSetup = (pathname) =>
  isBusinessConnectorSetupPath(pathname);

/**
 * Whether the business shell should hide its sidebar on the settings page.
 *
 * @example
 * shouldHideBusinessSidebarForSettings("/business/settings"); // true
 * shouldHideBusinessSidebarForSettings("/business/action-items"); // false
 */
export const shouldHideBusinessSidebarForSettings = (pathname = "") => {
  const normalized = String(pathname || "").replace(/\/+$/, "") || "/";
  return normalized === ACTION_ITEMS_SETTINGS_PATH;
};

/**
 * Whether the business shell should hide its left sidebar.
 * Applies on settings and all workflow connect/setup routes.
 *
 * @example
 * shouldHideBusinessSidebar("/business/settings"); // true
 * shouldHideBusinessSidebar("/business/workflows/new/email-digest"); // true
 * shouldHideBusinessSidebar("/business/workflows/abc-123"); // true
 * shouldHideBusinessSidebar("/business/action-items"); // false
 */
export const shouldHideBusinessSidebar = (pathname = "") =>
  isBusinessSettingsRoute(pathname);

/**
 * Whether an empty account (no action items, no connectors) should be sent to
 * connect-sources onboarding. Skipped when the user just finished connector
 * setup, chose the paste-transcript escape, or picked manual entry from
 * onboarding (localStorage flag cleared on sign-in/sign-up).
 *
 * @example
 * shouldRedirectEmptyAccountToConnectOnboarding({
 *   hasFetchedActionItems: true,
 *   itemsLoading: false,
 *   itemsCount: 0,
 *   workflowsCount: 0,
 * });
 * // true
 *
 * @example
 * shouldRedirectEmptyAccountToConnectOnboarding({
 *   hasFetchedActionItems: true,
 *   itemsLoading: false,
 *   itemsCount: 0,
 *   workflowsCount: 0,
 *   search: "?source=setup&setupAt=1",
 * });
 * // false — just finished connecting; do not bounce to onboarding
 *
 * @example
 * shouldRedirectEmptyAccountToConnectOnboarding({
 *   hasFetchedActionItems: true,
 *   itemsLoading: false,
 *   itemsCount: 0,
 *   workflowsCount: 0,
 *   search: "?pasteTranscript=1",
 * });
 * // false — intentional paste escape from onboarding
 *
 * @example
 * shouldRedirectEmptyAccountToConnectOnboarding({
 *   hasFetchedActionItems: true,
 *   itemsLoading: false,
 *   itemsCount: 0,
 *   workflowsCount: 0,
 *   search: "",
 * });
 * // false when readManualActionItemsModePreferred() is true (manual escape)
 */
export const shouldRedirectEmptyAccountToConnectOnboarding = ({
  hasFetchedActionItems,
  itemsLoading,
  itemsCount = 0,
  workflowsCount = 0,
  enableActionItemsAi = true,
  search = "",
} = {}) =>
  Boolean(
    enableActionItemsAi &&
      hasFetchedActionItems &&
      !itemsLoading &&
      itemsCount === 0 &&
      workflowsCount === 0 &&
      !isPostConnectorSetupActionItemsSearch(search) &&
      !readManualActionItemsModePreferred()
  );

/**
 * Full-screen gate for action-items home while checking connect onboarding.
 * Empty accounts (0 items + 0 connectors) redirect to /onboarding unless the
 * URL is a fresh post-setup / first-pull / paste-transcript escape.
 *
 * @example
 * resolveConnectSourcesOnboardingGate({
 *   hasFetchedActionItems: false,
 *   itemsLoading: true,
 *   itemsCount: 0,
 *   workflowsCount: 0,
 * });
 * // { showGate: true, shouldRedirect: false }
 *
 * @example
 * resolveConnectSourcesOnboardingGate({
 *   hasFetchedActionItems: true,
 *   itemsLoading: false,
 *   itemsCount: 0,
 *   workflowsCount: 1,
 * });
 * // { showGate: false, shouldRedirect: false }
 */
export const resolveConnectSourcesOnboardingGate = ({
  hasFetchedActionItems,
  itemsLoading,
  itemsCount = 0,
  workflowsCount = 0,
  enableActionItemsAi = true,
  search = "",
} = {}) => {
  if (!enableActionItemsAi) {
    return { showGate: false, shouldRedirect: false };
  }

  // Fresh post-setup / first-pull / paste-escape URLs must render; sidebar may
  // still say workflows=0 for a beat after connect.
  if (isPostConnectorSetupActionItemsSearch(search)) {
    return { showGate: false, shouldRedirect: false };
  }

  const isEmptyAccount = itemsCount === 0 && workflowsCount === 0;
  if (!isEmptyAccount) {
    return { showGate: false, shouldRedirect: false };
  }

  const shouldRedirect = shouldRedirectEmptyAccountToConnectOnboarding({
    hasFetchedActionItems,
    itemsLoading,
    itemsCount,
    workflowsCount,
    enableActionItemsAi,
    search,
  });

  return {
    showGate: !hasFetchedActionItems || itemsLoading || shouldRedirect,
    shouldRedirect,
  };
};

/** localStorage: user dismissed the first-time paste-transcript callout. */
export const PASTE_TRANSCRIPT_HINT_DISMISSED_KEY = "action_items_paste_transcript_hint_dismissed";

/**
 * Whether the first-time paste-transcript callout was dismissed or tasks arrived.
 *
 * @example
 * readPasteTranscriptHintDismissed(); // false when key unset
 */
export const readPasteTranscriptHintDismissed = () =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(PASTE_TRANSCRIPT_HINT_DISMISSED_KEY) === "1";

/**
 * Persist paste-transcript callout dismissal for this browser.
 *
 * @example
 * persistPasteTranscriptHintDismissed();
 * readPasteTranscriptHintDismissed(); // true
 */
export const persistPasteTranscriptHintDismissed = () => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PASTE_TRANSCRIPT_HINT_DISMISSED_KEY, "1");
};

/** Query param: show paste-transcript callout after leaving connect onboarding. */
export const PASTE_TRANSCRIPT_HINT_QUERY_PARAM = "pasteTranscript";

/** Query param: first visit to action items after onboarding connect flow. */
export const FIRST_PULL_FROM_ONBOARDING_QUERY_PARAM = "firstPull";

/**
 * Whether action-items URL means the user just left connector setup / onboarding
 * (or chose paste-transcript escape) and must not be bounced back to /onboarding.
 * Sidebar workflow state can still be stale (count 0) for a beat after first connect.
 *
 * @example
 * isPostConnectorSetupActionItemsSearch("?workflow=abc&source=setup&setupAt=1"); // true
 * isPostConnectorSetupActionItemsSearch("?firstPull=1"); // true
 * isPostConnectorSetupActionItemsSearch("?pasteTranscript=1"); // true
 * isPostConnectorSetupActionItemsSearch(""); // false
 */
export const isPostConnectorSetupActionItemsSearch = (search = "") => {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  if (params.get("source") === "setup") return true;
  if (params.get(FIRST_PULL_FROM_ONBOARDING_QUERY_PARAM) === "1") return true;
  if (params.get(PASTE_TRANSCRIPT_HINT_QUERY_PARAM) === "1") return true;
  return false;
};

/** localStorage: user has seen the first-pull processing screen. */
export const FIRST_PULL_FROM_ONBOARDING_SEEN_KEY = "action_items_first_pull_seen";

/** localStorage: last dismissed catch-up summary id for this browser. */
export const ACTION_ITEMS_CATCH_UP_DISMISSED_KEY = "action_items_catch_up_dismissed";
/** localStorage: aging-banner dismiss watermark (count at last dismiss). */
export const ACTION_ITEMS_AGING_BANNER_DISMISSED_COUNT_KEY =
  "action_items_aging_banner_dismissed_count";

/** Query param: filter list to new AI-ready items from catch-up CTA. */
export const CATCH_UP_AUTOMATABLE_QUERY_PARAM = "catchUp";

/** Query param value for automatable catch-up filter. */
export const CATCH_UP_AUTOMATABLE_VALUE = "automatable";

/** Query param: ISO cutoff for catch-up automatable filter. */
export const CATCH_UP_SINCE_AT_QUERY_PARAM = "sinceAt";

/** Query param for AI-ready assist filter on the action-items list. */
export const ACTION_ITEMS_ASSIST_FILTER_QUERY_PARAM = "assist";

/** Query param: scope the list to all action items from one meeting occurrence. */
export const ACTION_ITEMS_MEETING_FILTER_QUERY_PARAM = "meeting";

/** Query param: comma-separated workflow ids to drop from an active meeting filter. */
export const ACTION_ITEMS_MEETING_EXCLUDE_QUERY_PARAM = "meetingExclude";

/** Filter value: show only AI-ready tasks (Get AI help eligible). */
export const ASSIST_FILTER_AI_READY = "ai-ready";

/** Sentinel for "all tasks" in assist filter dropdowns. */
export const ASSIST_FILTER_ALL = "__all__";

/** Query param: filter list to pending items flagged `is_aging` by the API. */
export const ACTION_ITEMS_AGING_FILTER_QUERY_PARAM = "aging";

/** Query param value that turns the aging filter on. */
export const ACTION_ITEMS_AGING_FILTER_VALUE = "1";

/**
 * True when the action-items URL applies the aging (quiet pending) filter.
 *
 * @example
 * isAgingFilterActiveFromSearch("?aging=1"); // true
 * isAgingFilterActiveFromSearch("?category=Work"); // false
 */
export const isAgingFilterActiveFromSearch = (search = "") =>
  new URLSearchParams(String(search || "")).get(ACTION_ITEMS_AGING_FILTER_QUERY_PARAM) ===
  ACTION_ITEMS_AGING_FILTER_VALUE;

/** Query param: filter list to starred tasks (stored as `pinned` by the API). */
export const ACTION_ITEMS_STARRED_FILTER_QUERY_PARAM = "starred";

/**
 * True when the action-items URL applies the starred filter.
 *
 * @example
 * isStarredFilterActiveFromSearch("?starred=1"); // true
 * isStarredFilterActiveFromSearch("?aging=1"); // false
 */
export const isStarredFilterActiveFromSearch = (search = "") =>
  new URLSearchParams(String(search || "")).get(ACTION_ITEMS_STARRED_FILTER_QUERY_PARAM) === "1";

/**
 * Keep only starred tasks when the starred filter is on (no-op otherwise).
 *
 * @example
 * filterItemsByStarred([{ pinned: true }, { pinned: false }], true);
 * // [{ pinned: true }]
 */
export const filterItemsByStarred = (items, isStarredFilterActive) => {
  const list = Array.isArray(items) ? items : [];
  if (!isStarredFilterActive) return list;
  return list.filter((item) => Boolean(item?.pinned));
};

/**
 * True when AI Assist can run for one action item (matches ActionItemRow).
 *
 * @example
 * isActionItemAiAssistEligible({ ai_assist_type: "email" }); // true
 * isActionItemAiAssistEligible({ ai_assist_type: "other" }); // false
 */
export const isActionItemAiAssistEligible = (item) => {
  const assistType = String(item?.ai_assist_type || "").trim().toLowerCase();
  return assistType !== "other";
};

/**
 * True when the AI Actions control should be offered for this task. Hidden when the
 * classifier marked it "other", and for tasks added by hand that were never classified
 * (the run has nothing to act on there), so the control never leads to a dead end.
 *
 * @example
 * isAiActionSupported({ ai_assist_type: "email" }); // true
 * isAiActionSupported({ ai_assist_type: "other" }); // false
 * isAiActionSupported({ file_id: "manual::1", ai_assist_type: null }); // false
 * isAiActionSupported({ file_id: "msg-1", ai_assist_type: null }); // true (older synced task)
 */
export const isAiActionSupported = (item) => {
  if (!isActionItemAiAssistEligible(item)) return false;
  const assistType = String(item?.ai_assist_type || "").trim();
  if (!assistType && (isManualActionItem(item) || isCaptureWorkflowItem(item))) return false;
  return true;
};

/**
 * Label for the button that opens the original source inline, by source type.
 *
 * @example
 * resolveReadSourceLabel("granola"); // "Read transcript"
 * resolveReadSourceLabel("gmail"); // "Read email"
 */
export const resolveReadSourceLabel = (sourceType) => {
  switch (String(sourceType || "").trim()) {
    case "gmail":
      return "Read email";
    case "slack":
      return "Read messages";
    case "notion":
      return "Read page";
    case "manual":
      return "Read original";
    default:
      return "Read transcript";
  }
};

/**
 * True when the action-items URL applies the AI-ready assist filter.
 *
 * @example
 * isAiReadyAssistFilterActive("?assist=ai-ready"); // true
 */
export const isAiReadyAssistFilterActive = (search = "") => {
  const params = new URLSearchParams(String(search || ""));
  if (params.get(ACTION_ITEMS_ASSIST_FILTER_QUERY_PARAM) === ASSIST_FILTER_AI_READY) {
    return true;
  }
  return params.get(CATCH_UP_AUTOMATABLE_QUERY_PARAM) === CATCH_UP_AUTOMATABLE_VALUE;
};

/**
 * Parse optional sinceAt cutoff from the action-items URL.
 *
 * @example
 * parseAssistFilterSinceAtMs("?sinceAt=2026-06-28T09:00:00Z"); // epoch ms
 */
export const parseAssistFilterSinceAtMs = (search = "") => {
  const raw = new URLSearchParams(String(search || "")).get(CATCH_UP_SINCE_AT_QUERY_PARAM);
  const parsed = Date.parse(String(raw || ""));
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

/**
 * Filter action items by AI-ready assist eligibility and optional created_at cutoff.
 *
 * @example
 * filterItemsByAssistFilter(items, ASSIST_FILTER_AI_READY);
 * // pending/done rows where ai_assist_type !== "other"
 */
export const filterItemsByAssistFilter = (list, assistFilterId, { sinceAtMs } = {}) => {
  const filterId = String(assistFilterId || "").trim();
  const useAiReady = filterId === ASSIST_FILTER_AI_READY;
  const useSince = Number.isFinite(sinceAtMs);
  if (!useAiReady && !useSince) {
    return list;
  }
  return list.filter((item) => {
    if (useAiReady && !isActionItemAiAssistEligible(item)) {
      return false;
    }
    if (useSince) {
      const createdMs = Date.parse(String(item.created_at || ""));
      if (!Number.isFinite(createdMs) || createdMs <= sinceAtMs) {
        return false;
      }
    }
    return true;
  });
};

/**
 * Build assist-filter options for the action-items header menu.
 *
 * @example
 * buildActionItemsAssistFilterOptions([{ status: "pending", ai_assist_type: "email" }]);
 * // [{ value: "__all__", label: "All tasks (1)" }, { value: "ai-ready", label: "AI-ready only (1)" }]
 */
export const buildActionItemsAssistFilterOptions = (items = []) => {
  const itemList = Array.isArray(items) ? items : [];
  const pending = itemList.filter(
    (item) => String(item?.status || "").trim().toLowerCase() === ACTION_ITEM_STATUS_PENDING
  );
  const aiReadyCount = pending.filter(isActionItemAiAssistEligible).length;
  return [
    { value: ASSIST_FILTER_ALL, label: `All tasks (${pending.length})` },
    { value: ASSIST_FILTER_AI_READY, label: `AI Assist only (${aiReadyCount})` },
  ];
};

/**
 * Whether the user already saw the first-pull processing screen.
 *
 * @example
 * readFirstPullFromOnboardingSeen(); // false when key unset
 */
export const readFirstPullFromOnboardingSeen = () =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(FIRST_PULL_FROM_ONBOARDING_SEEN_KEY) === "1";

/**
 * Persist first-pull processing screen completion for this browser.
 *
 * @example
 * persistFirstPullFromOnboardingSeen();
 * readFirstPullFromOnboardingSeen(); // true
 */
export const persistFirstPullFromOnboardingSeen = () => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(FIRST_PULL_FROM_ONBOARDING_SEEN_KEY, "1");
};

/**
 * Read the catch-up summary id dismissed in this browser.
 *
 * @example
 * readCatchUpDismissedSummaryId(); // "abc123" or ""
 */
export const readCatchUpDismissedSummaryId = () => {
  if (typeof window === "undefined") return "";
  return String(window.localStorage.getItem(ACTION_ITEMS_CATCH_UP_DISMISSED_KEY) || "").trim();
};

/**
 * Persist the dismissed catch-up summary id for this browser.
 *
 * @example
 * persistCatchUpDismissedSummaryId("abc123");
 */
export const persistCatchUpDismissedSummaryId = (summaryId) => {
  if (typeof window === "undefined") return;
  const normalized = String(summaryId || "").trim();
  if (!normalized) return;
  window.localStorage.setItem(ACTION_ITEMS_CATCH_UP_DISMISSED_KEY, normalized);
};

/**
 * Parse a stored aging-banner dismiss watermark.
 *
 * @example
 * parseAgingBannerDismissedCount("3"); // 3
 * parseAgingBannerDismissedCount("nope"); // 0
 */
const parseAgingBannerDismissedCount = (raw) => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

/**
 * Read the aging-banner dismiss watermark (count at last dismiss).
 *
 * Survives login, tab close, and app relaunch. The banner reappears only when
 * the current aging pending count rises above this value.
 *
 * @example
 * readAgingBannerDismissedCount(); // 0 or 3
 */
export const readAgingBannerDismissedCount = () => {
  if (typeof window === "undefined") return 0;
  const fromLocal = parseAgingBannerDismissedCount(
    window.localStorage.getItem(ACTION_ITEMS_AGING_BANNER_DISMISSED_COUNT_KEY)
  );
  if (fromLocal > 0) return fromLocal;
  // Migrate a same-tab dismiss written before this used localStorage.
  const fromSession = parseAgingBannerDismissedCount(
    window.sessionStorage.getItem(ACTION_ITEMS_AGING_BANNER_DISMISSED_COUNT_KEY)
  );
  if (fromSession > 0) {
    window.localStorage.setItem(
      ACTION_ITEMS_AGING_BANNER_DISMISSED_COUNT_KEY,
      String(fromSession)
    );
    window.sessionStorage.removeItem(ACTION_ITEMS_AGING_BANNER_DISMISSED_COUNT_KEY);
  }
  return fromSession;
};

/**
 * Persist the aging pending count at dismiss time for this browser.
 *
 * @example
 * persistAgingBannerDismissedCount(3);
 */
export const persistAgingBannerDismissedCount = (count) => {
  if (typeof window === "undefined") return;
  const normalized = Math.max(0, Math.floor(Number(count) || 0));
  window.localStorage.setItem(
    ACTION_ITEMS_AGING_BANNER_DISMISSED_COUNT_KEY,
    String(normalized)
  );
  window.sessionStorage.removeItem(ACTION_ITEMS_AGING_BANNER_DISMISSED_COUNT_KEY);
};

/**
 * True when the action-items URL requests the catch-up automatable filter.
 *
 * @example
 * isCatchUpAutomatableDeepLink("?catchUp=automatable"); // true
 */
export const isCatchUpAutomatableDeepLink = (search = "") =>
  isAiReadyAssistFilterActive(search);

/**
 * Build a deep link that filters to new AI-ready action items.
 *
 * @example
 * buildCatchUpAutomatablePath("2026-06-28T09:00:00+00:00");
 * // "/business/action-items?assist=ai-ready&sinceAt=2026-06-28T09:00:00%2B00:00"
 */
export const buildCatchUpAutomatablePath = (sinceAt = "", { workflow = "", category = "" } = {}) =>
  buildActionItemsListPath({
    workflow,
    category,
    assist: ASSIST_FILTER_AI_READY,
    sinceAt,
  });

/**
 * True when the action-items URL requests the first-pull processing screen.
 *
 * @example
 * isFirstPullFromOnboardingDeepLink("?firstPull=1"); // true
 */
export const isFirstPullFromOnboardingDeepLink = (search) =>
  new URLSearchParams(String(search || "")).get(FIRST_PULL_FROM_ONBOARDING_QUERY_PARAM) ===
  "1";

/**
 * True when the action-items URL requests the paste-transcript callout.
 *
 * @example
 * isPasteTranscriptHintDeepLink("?pasteTranscript=1"); // true
 */
export const isPasteTranscriptHintDeepLink = (search) =>
  new URLSearchParams(String(search || "")).get(PASTE_TRANSCRIPT_HINT_QUERY_PARAM) === "1";

/**
 * Clear paste-transcript callout dismissal (e.g. user chose paste from settings).
 *
 * @example
 * clearPasteTranscriptHintDismissed();
 * readPasteTranscriptHintDismissed(); // false
 */
export const clearPasteTranscriptHintDismissed = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(PASTE_TRANSCRIPT_HINT_DISMISSED_KEY);
};

/**
 * Clear manual action-items mode (e.g. after account deletion).
 *
 * @example
 * clearManualActionItemsModePreferred();
 * readManualActionItemsModePreferred(); // false
 */
export const clearManualActionItemsModePreferred = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(MANUAL_ACTION_ITEMS_MODE_KEY);
};

/**
 * Clear browser-local action-items onboarding flags for a fresh account.
 *
 * @example
 * clearActionItemsOnboardingLocalState();
 * readManualActionItemsModePreferred(); // false
 */
export const clearActionItemsOnboardingLocalState = () => {
  clearManualActionItemsModePreferred();
  clearPasteTranscriptHintDismissed();
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(POST_SETUP_WORKFLOW_FOCUS_KEY);
  window.localStorage.removeItem(FIRST_PULL_FROM_ONBOARDING_SEEN_KEY);
  window.localStorage.removeItem(ACTION_ITEMS_CATCH_UP_DISMISSED_KEY);
};

/** Open / TO DO status — sidebar source counts include only items in this state. */
export const ACTION_ITEM_STATUS_PENDING = "pending";

/**
 * Apply the toolbar workflow query to the global list.
 *
 * @example
 * filterItemsByWorkflowQuery(items, "wf-zoom");
 * // digest items for zoom only (no manual:: rows)
 */
export const filterItemsByWorkflowQuery = (list, workflowFilterId) => {
  const filterId = String(workflowFilterId || "").trim();
  if (!filterId) return list;
  if (filterId === MANUAL_WORKFLOW_FILTER_ID || filterId === CAPTURE_WORKFLOW_ID) {
    return list.filter(isManualActionItem);
  }
  return list.filter(
    (item) =>
      String(item.workflow_id || "").trim() === filterId && !isManualActionItem(item)
  );
};

/**
 * Workflow-filter id to apply when the user filters from a task's source row.
 *
 * Manual / capture items map to the synthetic "Added by you" filter; connector
 * items use their real `workflow_id`.
 *
 * @example
 * resolveActionItemSourceFilterId({ file_id: "manual::abc" });
 * // "__manual__"
 *
 * @example
 * resolveActionItemSourceFilterId({ workflow_id: "wf-granola" });
 * // "wf-granola"
 */
export const resolveActionItemSourceFilterId = (item) => {
  if (!item || typeof item !== "object") return "";
  if (isManualActionItem(item) || isCaptureWorkflowItem(item)) {
    return MANUAL_WORKFLOW_FILTER_ID;
  }
  return String(item.workflow_id || "").trim();
};

/**
 * Meetings have no stable id in this API, and different connectors (Zoom vs Granola,
 * say) independently stamp `meeting_from_label` off their own clock for the same
 * real-world meeting, so those labels can drift by a few minutes even when it's the
 * same call. Mirrors the backend dedup fuzzy window (see action_items_deduper.py),
 * scaled down since this is a user-facing filter rather than a merge decision.
 */
export const ACTION_ITEMS_MEETING_WINDOW_MINUTES = 15;

/**
 * Scope the list to every action item captured near the same meeting occurrence,
 * across all sources, using a +/- window around the reference meeting timestamp.
 *
 * @example
 * filterItemsByMeetingWindow(items, "05-05-2026 11:00 AM");
 * // every item within +/-15 minutes of that meeting, from any source
 */
export const filterItemsByMeetingWindow = (
  list,
  meetingAtLabel,
  { windowMinutes = ACTION_ITEMS_MEETING_WINDOW_MINUTES } = {}
) => {
  const centerMs = parseMeetingLabel(String(meetingAtLabel || "").trim());
  if (!centerMs) return [];
  const windowMs = windowMinutes * 60 * 1000;
  return list.filter((item) => {
    const itemMs = parseMeetingLabel(String(item.meeting_from_label || "").trim());
    return Boolean(itemMs) && Math.abs(itemMs - centerMs) <= windowMs;
  });
};

/**
 * Resolve `message_sent_at` for one row, falling back from `meeting_from_label`.
 *
 * @example
 * resolveActionItemMessageSentAt({ meeting_from_label: "28-07-2026 10:45 AM" });
 * // "2026-07-28T10:45:00.000Z"
 */
export const resolveActionItemMessageSentAt = (item) => {
  const direct = String(item?.message_sent_at || "").trim();
  if (direct) return direct;
  const label = String(item?.meeting_from_label || "").trim();
  if (!label) return "";
  const epochMs = parseMeetingLabel(label);
  return epochMs > 0 ? new Date(epochMs).toISOString() : "";
};

/**
 * Pick one anchor row for linking manual adds to the active meeting filter.
 *
 * @example
 * resolveMeetingAddAnchor({
 *   items: [{ meeting_from_label: "28-07-2026 10:45 AM", workflow_id: "wf-1" }],
 *   meetingFilterId: "28-07-2026 10:45 AM",
 * });
 * // the Granola/Zoom row in the window
 */
export const resolveMeetingAddAnchor = ({
  items = [],
  meetingFilterId = "",
  preferredItem = null,
  windowMinutes = ACTION_ITEMS_MEETING_WINDOW_MINUTES,
} = {}) => {
  const meetingLabel = String(meetingFilterId || "").trim();
  if (!meetingLabel) return null;

  const isCapturedMeetingFilter = (list) =>
    list.some(
      (entry) =>
        String(entry?.meeting_from_label || "").trim() === meetingLabel &&
        isManualActionItem(entry)
    );

  const pool = filterItemsByMeetingWindow(items, meetingLabel, {
    windowMinutes: isCapturedMeetingFilter(items) ? 0 : windowMinutes,
  });
  if (!pool.length) return null;

  const preferredStableId = preferredItem ? getItemStableId(preferredItem) : "";
  if (preferredStableId) {
    const preferredMatch = pool.find((entry) => getItemStableId(entry) === preferredStableId);
    if (preferredMatch) return preferredMatch;
  }

  const pipelineRows = pool.filter(
    (entry) =>
      !isManualActionItem(entry) && String(entry?.workflow_id || "").trim() !== CAPTURE_WORKFLOW_ID
  );
  const candidates = pipelineRows.length ? pipelineRows : pool;
  return [...candidates].sort(
    (left, right) => getActionItemSourceTimeMs(right) - getActionItemSourceTimeMs(left)
  )[0];
};

/**
 * Build meeting-link payload for manual task creation while a meeting filter is active.
 *
 * @example
 * buildMeetingAddContext({ meeting_from_label: "28-07-2026 10:45 AM", workflow_id: "wf-1" });
 * // { workflowId: "wf-1", meetingFromLabel: "28-07-2026 10:45 AM", messageSentAt: "..." }
 */
export const buildMeetingAddContext = (anchorItem) => {
  if (!anchorItem || typeof anchorItem !== "object") return null;
  const meetingFromLabel = String(anchorItem.meeting_from_label || "").trim();
  const messageSentAt = resolveActionItemMessageSentAt(anchorItem);
  if (!meetingFromLabel && !messageSentAt) return null;

  const workflowId = String(anchorItem.workflow_id || "").trim();
  const workflowName = String(anchorItem.workflow_name || "").trim();
  const meetingSubject = resolveActionItemMeetingSubject(anchorItem);

  return {
    workflowId:
      workflowId && workflowId !== CAPTURE_WORKFLOW_ID ? workflowId : "",
    workflowName,
    meetingFromLabel,
    messageSentAt,
    meetingSubject,
  };
};

/**
 * Label when new manual tasks will be linked to the active meeting filter.
 *
 * @example
 * formatMeetingAddContextHintLabel({ meetingFilterId: "28-07-2026 10:45 AM" });
 * // "Adds to this meeting · Tue, Jul 28 · 10:45 AM"
 */
export const formatMeetingAddContextHintLabel = ({
  meetingFilterId = "",
} = {}) => {
  const timeLabel = formatMeetingLabelForDisplay(String(meetingFilterId || "").trim());
  if (timeLabel) return `Adds to this meeting · ${timeLabel}`;
  return "Adds to this meeting";
};

/** Label when the user opted out of linking manual adds to the meeting filter. */
export const MEETING_ADD_UNLINKED_HINT_LABEL =
  "Added by you — won't show in this meeting";

/** Compact action label to re-enable meeting linking for manual adds. */
export const MEETING_ADD_RELINK_ACTION_LABEL = "Add to meeting";

/**
 * Distinct sources present in a meeting-scoped item list, for per-source filter chips.
 *
 * @example
 * buildMeetingSourceOptions([
 *   { workflow_id: "wf-zoom", workflow_name: "Zoom" },
 *   { workflow_id: "wf-granola", workflow_name: "Granola" },
 * ]);
 * // [{ id: "wf-zoom", name: "Zoom", count: 1 }, { id: "wf-granola", name: "Granola", count: 1 }]
 */
export const buildMeetingSourceOptions = (list) => {
  const byId = new Map();
  (list || []).forEach((item) => {
    const id = String(item?.workflow_id || "").trim();
    if (!id) return;
    const name = String(item?.workflow_name || "").trim() || id;
    if (!byId.has(id)) byId.set(id, { id, name, count: 0 });
    byId.get(id).count += 1;
  });
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
};

/**
 * Drop items from a set of excluded workflow ids (used to let a user remove one
 * source's items from a cross-source meeting filter without leaving the view).
 *
 * @example
 * filterItemsExcludingWorkflowIds(items, ["wf-granola"]);
 * // items with workflow_id !== "wf-granola"
 */
export const filterItemsExcludingWorkflowIds = (list, excludedWorkflowIds) => {
  const excluded = new Set(
    (excludedWorkflowIds || []).map((id) => String(id || "").trim()).filter(Boolean)
  );
  if (excluded.size === 0) return list;
  return list.filter((item) => !excluded.has(String(item?.workflow_id || "").trim()));
};

/**
 * True when the user is in settings or a workflow connect/setup flow (gear is the active nav).
 *
 * @example
 * isBusinessSettingsRoute("/business/settings");
 * // true
 *
 * @example
 * isBusinessSettingsRoute("/business/workflows/new/zoom-digest");
 * // true
 */
export const isBusinessSettingsRoute = (pathname = "") => {
  const path = String(pathname || "").trim();
  if (path === ACTION_ITEMS_SETTINGS_PATH) return true;
  return path.startsWith("/business/workflows");
};

/**
 * Build sidebar source-nav counts from action items and workflows (TO DO / pending only).
 *
 * Connector rows are ordered by pending count (highest first) so busy sources
 * stay above empty ones in the Source filter.
 *
 * @example
 * buildActionItemsSourceNav({
 *   items: [
 *     { workflow_id: "a", file_id: "x", status: "pending" },
 *     { workflow_id: "a", file_id: "y", status: "done" },
 *   ],
 *   workflows: [{ workflow_id: "a", name: "Zoom" }],
 * });
 * // { totalCount: 1, manualCount: 0, hasManualItems: false, workflowEntries: [{ id: "a", name: "Zoom", count: 1, workflowType: "" }] }
 */
export const buildActionItemsSourceNav = ({ items = [], workflows = [] } = {}) => {
  const itemList = Array.isArray(items) ? items : [];
  const workflowList = Array.isArray(workflows) ? workflows : [];
  const workflowNameById = new Map();
  workflowList.forEach((workflow) => {
    const id = String(workflow?.workflow_id || "").trim();
    if (!id || id === CAPTURE_WORKFLOW_ID) return;
    const name = String(workflow?.name || "").trim();
    if (name) workflowNameById.set(id, name);
  });
  itemList.forEach((item) => {
    const id = String(item.workflow_id || "").trim();
    if (!id || id === CAPTURE_WORKFLOW_ID) return;
    const name = String(item.workflow_name || "").trim();
    if (name) workflowNameById.set(id, name);
  });
  const workflowTypeById = new Map();
  workflowList.forEach((workflow) => {
    const id = String(workflow?.workflow_id || "").trim();
    const type = String(workflow?.workflow_type || "").trim();
    if (id && type) workflowTypeById.set(id, type);
  });
  itemList.forEach((item) => {
    const id = String(item.workflow_id || "").trim();
    const type = String(item.workflow_type || "").trim();
    if (id && type) workflowTypeById.set(id, type);
  });
  const workflowOptions = new Map();
  workflowList.forEach((workflow) => {
    const id = String(workflow?.workflow_id || "").trim();
    if (!id || id === CAPTURE_WORKFLOW_ID) return;
    const name = workflowNameById.get(id) || String(workflow?.name || "").trim() || id;
    workflowOptions.set(id, name);
  });
  itemList.forEach((item) => {
    const id = String(item.workflow_id || "").trim();
    if (!id || id === CAPTURE_WORKFLOW_ID) return;
    const name = workflowNameById.get(id) || id;
    workflowOptions.set(id, name);
  });
  const todoItems = itemList.filter(
    (item) => item?.status === ACTION_ITEM_STATUS_PENDING
  );
  const manualCount = todoItems.filter(isManualActionItem).length;
  const workflowEntries = [...workflowOptions.entries()]
    .map(([id, name]) => ({
      id,
      name,
      count: todoItems.filter(
        (item) => String(item.workflow_id || "").trim() === id && !isManualActionItem(item)
      ).length,
      workflowType: workflowTypeById.get(id) || "",
    }))
    .sort((left, right) => {
      if (right.count !== left.count) return right.count - left.count;
      return left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
    });
  return {
    totalCount: todoItems.length,
    manualCount,
    hasManualItems: itemList.some(isManualActionItem),
    workflowEntries,
  };
};

/**
 * Build an in-app path to the global action-items list.
 *
 * @example
 * buildActionItemsListPath({ workflow: "wf-zoom", category: "Client follow-ups" });
 * // "/business/action-items?workflow=wf-zoom&category=Client+follow-ups"
 */
export const buildActionItemsListPath = ({
  workflow = "",
  category = "",
  assist = "",
  aging = false,
  starred = false,
  meeting = "",
  meetingExclude = [],
  sinceAt = "",
  pasteTranscript = false,
  firstPull = false,
} = {}) => {
  const params = new URLSearchParams();
  const workflowId = String(workflow || "").trim();
  if (workflowId) {
    params.set("workflow", workflowId);
  }
  const categoryId = String(category || "").trim();
  if (categoryId) {
    params.set("category", categoryId);
  }
  const meetingId = String(meeting || "").trim();
  if (meetingId) {
    params.set(ACTION_ITEMS_MEETING_FILTER_QUERY_PARAM, meetingId);
  }
  const meetingExcludeIds = (Array.isArray(meetingExclude) ? meetingExclude : [meetingExclude])
    .map((id) => String(id || "").trim())
    .filter(Boolean);
  if (meetingId && meetingExcludeIds.length) {
    params.set(ACTION_ITEMS_MEETING_EXCLUDE_QUERY_PARAM, meetingExcludeIds.join(","));
  }
  const assistId = String(assist || "").trim();
  if (assistId === ASSIST_FILTER_AI_READY) {
    params.set(ACTION_ITEMS_ASSIST_FILTER_QUERY_PARAM, ASSIST_FILTER_AI_READY);
  }
  if (aging) {
    params.set(ACTION_ITEMS_AGING_FILTER_QUERY_PARAM, ACTION_ITEMS_AGING_FILTER_VALUE);
  }
  if (starred) {
    params.set(ACTION_ITEMS_STARRED_FILTER_QUERY_PARAM, "1");
  }
  const sinceAtValue = String(sinceAt || "").trim();
  if (sinceAtValue) {
    params.set(CATCH_UP_SINCE_AT_QUERY_PARAM, sinceAtValue);
  }
  if (pasteTranscript) {
    params.set(PASTE_TRANSCRIPT_HINT_QUERY_PARAM, "1");
  }
  if (firstPull) {
    params.set(FIRST_PULL_FROM_ONBOARDING_QUERY_PARAM, "1");
  }
  const qs = params.toString();
  return qs ? `/business/action-items?${qs}` : "/business/action-items";
};

/**
 * Human-readable labels for the active source/category filters.
 *
 * @example
 * resolveActionItemsFilterSummary({
 *   workflowFilterId: "wf-1",
 *   categoryFilterId: "Work",
 *   sourceNav: { workflowEntries: [{ id: "wf-1", name: "Zoom" }] },
 * });
 * // { sourceLabel: "Zoom", categoryLabel: "Work", isActive: true }
 */
export const resolveActionItemsFilterSummary = ({
  workflowFilterId = "",
  categoryFilterId = "",
  assistFilterId = "",
  agingFilterActive = false,
  starredFilterActive = false,
  sourceNav = {},
} = {}) => {
  const workflowId = String(workflowFilterId || "").trim();
  const categoryId = String(categoryFilterId || "").trim();
  const hasCategory = Boolean(categoryId);
  const hasWorkflow = Boolean(workflowId);
  const hasAssist = String(assistFilterId || "").trim() === ASSIST_FILTER_AI_READY;
  const hasAging = Boolean(agingFilterActive);
  const hasStarred = Boolean(starredFilterActive);
  if (!hasWorkflow && !hasCategory && !hasAssist && !hasAging && !hasStarred) {
    return {
      sourceLabel: "",
      categoryLabel: "",
      assistLabel: "",
      agingLabel: "",
      starredLabel: "",
      isActive: false,
    };
  }
  let sourceLabel = "";
  if (hasWorkflow) {
    if (workflowId === MANUAL_WORKFLOW_FILTER_ID || workflowId === CAPTURE_WORKFLOW_ID) {
      sourceLabel = "Added by you";
    } else {
      const entry = (sourceNav.workflowEntries || []).find((row) => row.id === workflowId);
      sourceLabel = entry?.name || workflowId;
    }
  }
  return {
    sourceLabel,
    categoryLabel: hasCategory ? categoryId : "",
    assistLabel: hasAssist ? "AI Assist only" : "",
    agingLabel: hasAging ? "Aging" : "",
    starredLabel: hasStarred ? "Starred" : "",
    isActive: hasWorkflow || hasCategory || hasAssist || hasAging || hasStarred,
  };
};

/**
 * Split a connector/workflow display name into a short headline and identity subtext,
 * e.g. so a filter chip can read "Gmail" with "hcoshik98@gmail.com" underneath.
 *
 * @example
 * splitActionItemsSourceLabel("Gmail - hcoshik98@gmail.com");
 * // { main: "Gmail", sub: "hcoshik98@gmail.com" }
 *
 * @example
 * splitActionItemsSourceLabel("Zoom");
 * // { main: "Zoom", sub: "" }
 */
export const splitActionItemsSourceLabel = (name = "") => {
  const raw = String(name || "").trim();
  const match = raw.match(/^(.+?)\s+-\s+(.+)$/);
  if (!match) return { main: raw, sub: "" };
  return { main: match[1].trim(), sub: match[2].trim() };
};

/** Query param: first-time profile setup banner on settings page. */
export const ACTION_ITEMS_SETUP_PROFILE_QUERY_PARAM = "setupProfile";

/**
 * True when settings should highlight profile setup (first login).
 *
 * @example
 * isActionItemsSetupProfileDeepLink("?setupProfile=1");
 * // true
 */
export const isActionItemsSetupProfileDeepLink = (search = "") => {
  const raw = new URLSearchParams(String(search || "").replace(/^\?/, "")).get(
    ACTION_ITEMS_SETUP_PROFILE_QUERY_PARAM
  );
  return raw === "1" || raw === "true";
};

/**
 * Query param that focuses one connector on the settings page. Deliberately NOT `workflow`:
 * that name belongs to the task-list source filter, and sharing it let the list filter leak
 * into settings (and settings focus leak back into the list).
 */
export const ACTION_ITEMS_SETTINGS_WORKFLOW_QUERY_PARAM = "settingsWorkflow";

/** Query param that opens settings (legacy; action-items deep links redirect to ACTION_ITEMS_SETTINGS_PATH). */
export const ACTION_ITEMS_SETTINGS_QUERY_PARAM = "settings";

/** Query param for the active settings tab (`general` | `connect-apps` | `keyboard-shortcuts`). */
export const ACTION_ITEMS_SETTINGS_TAB_QUERY_PARAM = "settingsTab";

/** Optional query param to focus a connect-app card (`email-digest`, `zoom-digest`, `granola-digest`). */
export const ACTION_ITEMS_SETTINGS_APP_QUERY_PARAM = "settingsApp";

/** Root settings screen — lists General/Data Sources for drill-down navigation. */
export const ACTION_ITEMS_SETTINGS_TAB_ROOT = "";

/** General settings tab — profile, defaults, AI assist, categories. */
export const ACTION_ITEMS_SETTINGS_TAB_GENERAL = "general";

/** Connect Apps tab — workflow/source connection catalog. */
export const ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS = "connect-apps";

/** Keyboard shortcuts tab — view and remap action-items hotkeys. */
export const ACTION_ITEMS_SETTINGS_TAB_KEYBOARD = "keyboard-shortcuts";

/** Valid settings tab ids for deep-link parsing. */
export const ACTION_ITEMS_SETTINGS_TABS = [
  ACTION_ITEMS_SETTINGS_TAB_GENERAL,
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  ACTION_ITEMS_SETTINGS_TAB_KEYBOARD,
];

/** Query param: one-shot reset of profile + AI context onboarding (dev / QA). */
export const PERFORMER_ONBOARDING_RESET_QUERY_PARAM = "resetPerformerOnboarding";

/**
 * True when the URL requests a performer onboarding reset on load.
 *
 * @example
 * isPerformerOnboardingResetDeepLink("?resetPerformerOnboarding=1");
 * // true
 */
export const isPerformerOnboardingResetDeepLink = (search = "") => {
  const raw = new URLSearchParams(String(search || "").replace(/^\?/, "")).get(
    PERFORMER_ONBOARDING_RESET_QUERY_PARAM
  );
  return raw === "1" || raw === "true";
};

/**
 * True when the URL requests the AI assist settings drawer (extension "AI settings" link).
 *
 * @example
 * isActionItemsAiSettingsDeepLink("?aiSettings=1");
 * // true
 *
 * @example
 * isActionItemsAiSettingsDeepLink("?workflow=wf-1");
 * // false
 */
export const isActionItemsAiSettingsDeepLink = (search = "") => {
  const raw = new URLSearchParams(String(search || "").replace(/^\?/, "")).get(
    ACTION_ITEMS_AI_SETTINGS_QUERY_PARAM
  );
  return raw === "1" || raw === "true";
};

/**
 * Parse the requested settings tab from URL search params.
 *
 * @example
 * parseActionItemsSettingsTab("?settingsTab=connect-apps");
 * // "connect-apps"
 *
 * @example
 * parseActionItemsSettingsTab("?settings=1");
 * // "" (root)
 */
export const parseActionItemsSettingsTab = (search = "") => {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const raw = String(params.get(ACTION_ITEMS_SETTINGS_TAB_QUERY_PARAM) || "").trim();
  if (raw === ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS) {
    return ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS;
  }
  if (raw === ACTION_ITEMS_SETTINGS_TAB_GENERAL) {
    return ACTION_ITEMS_SETTINGS_TAB_GENERAL;
  }
  if (raw === ACTION_ITEMS_SETTINGS_TAB_KEYBOARD) {
    return ACTION_ITEMS_SETTINGS_TAB_KEYBOARD;
  }
  return ACTION_ITEMS_SETTINGS_TAB_ROOT;
};

/**
 * Parse optional connect-app focus slug from URL search params.
 *
 * @example
 * parseActionItemsSettingsApp("?settingsApp=zoom-digest");
 * // "zoom-digest"
 */
export const parseActionItemsSettingsApp = (search = "") =>
  String(
    new URLSearchParams(String(search || "").replace(/^\?/, "")).get(
      ACTION_ITEMS_SETTINGS_APP_QUERY_PARAM
    ) || ""
  ).trim();

/**
 * True when the URL requests the unified settings drawer (any tab).
 *
 * @example
 * isActionItemsSettingsDeepLink("?settings=1&settingsTab=connect-apps");
 * // true
 *
 * @example
 * isActionItemsSettingsDeepLink("?aiSettings=1");
 * // true (legacy alias for General tab)
 */
export const isActionItemsSettingsDeepLink = (search = "") => {
  if (isActionItemsAiSettingsDeepLink(search)) return true;
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const settingsFlag = params.get(ACTION_ITEMS_SETTINGS_QUERY_PARAM);
  if (settingsFlag === "1" || settingsFlag === "true") return true;
  const tab = String(params.get(ACTION_ITEMS_SETTINGS_TAB_QUERY_PARAM) || "").trim();
  return ACTION_ITEMS_SETTINGS_TABS.includes(tab);
};

/**
 * Connector id to focus on the settings page (`settingsWorkflow`).
 *
 * @example
 * parseActionItemsSettingsWorkflow("?settingsTab=connect-apps&settingsWorkflow=wf-1");
 * // "wf-1"
 *
 * @example
 * parseActionItemsSettingsWorkflow("?workflow=wf-1");
 * // "" (the list filter is not a settings param)
 */
export const parseActionItemsSettingsWorkflow = (search = "") =>
  String(
    new URLSearchParams(String(search || "").replace(/^\?/, "")).get(
      ACTION_ITEMS_SETTINGS_WORKFLOW_QUERY_PARAM
    ) || ""
  ).trim();

/** Query params that make up the task-list filter state (and nothing else). */
const ACTION_ITEMS_LIST_FILTER_QUERY_PARAMS = [
  "workflow",
  "category",
  ACTION_ITEMS_ASSIST_FILTER_QUERY_PARAM,
  ACTION_ITEMS_AGING_FILTER_QUERY_PARAM,
  ACTION_ITEMS_STARRED_FILTER_QUERY_PARAM,
  ACTION_ITEMS_MEETING_FILTER_QUERY_PARAM,
  ACTION_ITEMS_MEETING_EXCLUDE_QUERY_PARAM,
  CATCH_UP_SINCE_AT_QUERY_PARAM,
];

/**
 * Keep only the task-list filter params from a search string, so a round trip through
 * settings restores the filter without carrying one-shot params (source=setup, code, ...).
 *
 * @example
 * pickActionItemsListFilterSearch("?workflow=wf-1&source=setup&setupAt=1&code=abc");
 * // "?workflow=wf-1"
 *
 * @example
 * pickActionItemsListFilterSearch("?settingsTab=general");
 * // ""
 */
export const pickActionItemsListFilterSearch = (search = "") => {
  const source = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const next = new URLSearchParams();
  ACTION_ITEMS_LIST_FILTER_QUERY_PARAMS.forEach((key) => {
    const value = String(source.get(key) || "").trim();
    if (value) next.set(key, value);
  });
  const qs = next.toString();
  return qs ? `?${qs}` : "";
};

/**
 * Build an in-app path to the settings page (no host).
 *
 * @example
 * buildActionItemsSettingsPath({ tab: "connect-apps", app: "zoom-digest" });
 * // "/business/settings?settingsTab=connect-apps&settingsApp=zoom-digest"
 *
 * @example
 * buildActionItemsSettingsPath({ tab: "connect-apps", workflow: "wf-1" });
 * // "/business/settings?settingsTab=connect-apps&settingsWorkflow=wf-1"
 */
export const buildActionItemsSettingsPath = ({
  tab = ACTION_ITEMS_SETTINGS_TAB_ROOT,
  app = "",
  workflow = "",
  setupProfile = false,
} = {}) => {
  const resolvedTab = ACTION_ITEMS_SETTINGS_TABS.includes(tab)
    ? tab
    : ACTION_ITEMS_SETTINGS_TAB_ROOT;
  const params = new URLSearchParams();
  if (resolvedTab !== ACTION_ITEMS_SETTINGS_TAB_ROOT) {
    params.set(ACTION_ITEMS_SETTINGS_TAB_QUERY_PARAM, resolvedTab);
  }
  const appSlug = String(app || "").trim();
  if (appSlug) {
    params.set(ACTION_ITEMS_SETTINGS_APP_QUERY_PARAM, appSlug);
  }
  const workflowId = String(workflow || "").trim();
  if (workflowId) {
    params.set(ACTION_ITEMS_SETTINGS_WORKFLOW_QUERY_PARAM, workflowId);
  }
  if (setupProfile) {
    params.set(ACTION_ITEMS_SETTINGS_TAB_QUERY_PARAM, ACTION_ITEMS_SETTINGS_TAB_GENERAL);
    params.set(ACTION_ITEMS_SETUP_PROFILE_QUERY_PARAM, "1");
  }
  const qs = params.toString();
  return qs ? `${ACTION_ITEMS_SETTINGS_PATH}?${qs}` : ACTION_ITEMS_SETTINGS_PATH;
};

/**
 * Web app base URL from env (`REACT_APP_ENV` / `REACT_APP_REDIRECT_URI`).
 *
 * @example
 * resolveWebappBaseUrl();
 * // "http://localhost:3000" in development
 *
 * @example
 * resolveWebappBaseUrl();
 * // "https://app.taskmantis.com" when REACT_APP_ENV=staging
 */
export const resolveWebappBaseUrl = () => String(config.REDIRECT_URI || "").trim();

/**
 * Full settings URL when the extension opens the web app in a new tab.
 *
 * @example
 * buildExtensionActionItemsSettingsUrl({ tab: "general" });
 * // "http://localhost:3000/business/settings?settingsTab=general&ext=1"
 */
export const buildExtensionActionItemsSettingsUrl = ({
  tab = ACTION_ITEMS_SETTINGS_TAB_GENERAL,
  app = "",
  workflow = "",
} = {}) =>
  buildActionItemsSettingsUrl(resolveWebappBaseUrl(), {
    tab,
    app,
    workflow,
    includeExt: true,
  });

/**
 * Build a webapp URL that opens unified settings (dedicated page).
 *
 * @example
 * buildActionItemsSettingsUrl("https://app.taskmantis.com", { tab: "connect-apps", app: "zoom-digest" });
 * // "https://app.taskmantis.com/business/settings?settingsTab=connect-apps&settingsApp=zoom-digest&ext=1"
 *
 * @example
 * buildActionItemsSettingsUrl("", { tab: "general" });
 * // "/business/settings?settingsTab=general"
 */
export const buildActionItemsSettingsUrl = (
  baseUrl = "",
  {
    tab = ACTION_ITEMS_SETTINGS_TAB_ROOT,
    app = "",
    includeExt = true,
    workflow = "",
  } = {}
) => {
  const normalizedBase = String(baseUrl || "").trim().replace(/\/$/, "");
  const path = buildActionItemsSettingsPath({ tab, app, workflow });
  if (!normalizedBase) {
    return path;
  }
  const url = new URL(path, normalizedBase);
  if (includeExt) {
    url.searchParams.set(EXTENSION_OPENED_QUERY_PARAM, EXTENSION_OPENED_QUERY_VALUE);
  }
  return url.toString();
};

/**
 * Webapp URL for the global action-items page with AI assist settings open on load.
 *
 * @example
 * buildActionItemsAiSettingsUrl("https://app.taskmantis.com");
 * // "https://app.taskmantis.com/business/action-items?settings=1&settingsTab=general&ext=1"
 */
export const buildActionItemsAiSettingsUrl = (baseUrl = "") =>
  buildActionItemsSettingsUrl(baseUrl, { tab: ACTION_ITEMS_SETTINGS_TAB_GENERAL });

const _HIGH_URGENCY_KEYWORDS = [
  "asap", "urgent", "urgently", "immediately", "critical",
  "blocker", "blocking", "overdue", "past due", "right away",
];

/**
 * Parse a due_date string ("YYYY-MM-DD") into a midnight-local Date, or null.
 *
 * @example
 * parseActionItemDueDate("2026-05-28");
 * // Date at local midnight on 2026-05-28
 *
 * @example
 * parseActionItemDueDate("2026-05-28T12:00:00Z");
 * // Date at local midnight on 2026-05-28 (time portion ignored)
 */
export const parseActionItemDueDate = (dueDateStr) => {
  const s = String(dueDateStr || "").trim().slice(0, 10);
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
};

/**
 * Compare two action items by due date for list ordering.
 * Items without a due date sort after dated items. Equal due dates return 0
 * (use `compareActionItemsDefaultSortTieBreak` as the sort tie-breaker).
 *
 * @example
 * compareActionItemsByDueDate(
 *   { due_date: "2026-05-28" },
 *   { due_date: "2026-05-29" },
 *   { direction: "soonest" }
 * );
 * // negative number (May 28 before May 29)
 */
export const compareActionItemsByDueDate = (a, b, { direction = "soonest" } = {}) => {
  const aDue = parseActionItemDueDate(a?.due_date);
  const bDue = parseActionItemDueDate(b?.due_date);
  const soonest = direction !== "latest";
  if (!aDue && !bDue) return 0;
  if (!aDue) return 1;
  if (!bDue) return -1;
  const diff = aDue.getTime() - bDue.getTime();
  if (diff === 0) return 0;
  return soonest ? diff : -diff;
};

/**
 * Return a new array sorted by due date (soonest or latest first).
 *
 * @example
 * sortActionItemsByDueDate(
 *   [{ due_date: "2026-06-01" }, { due_date: "2026-05-20" }],
 *   "soonest"
 * );
 * // [{ due_date: "2026-05-20" }, { due_date: "2026-06-01" }]
 */
export const sortActionItemsByDueDate = (list, direction = "soonest", tieBreak) => {
  const sorted = Array.isArray(list) ? [...list] : [];
  sorted.sort((a, b) => {
    const cmp = compareActionItemsByDueDate(a, b, { direction });
    if (cmp !== 0) return cmp;
    return typeof tieBreak === "function" ? tieBreak(a, b) : 0;
  });
  return sorted;
};

export const ACTION_ITEM_SORT_DEFAULT = "default";
export const ACTION_ITEM_SORT_ACTION_ASC = "action_asc";
export const ACTION_ITEM_SORT_ACTION_DESC = "action_desc";
export const ACTION_ITEM_SORT_CATEGORY_ASC = "category_asc";
export const ACTION_ITEM_SORT_CATEGORY_DESC = "category_desc";
export const ACTION_ITEM_SORT_SOURCE_NEWEST = "source_newest";
export const ACTION_ITEM_SORT_SOURCE_OLDEST = "source_oldest";
export const ACTION_ITEM_SORT_DUE_SOONEST = "due_soonest";
export const ACTION_ITEM_SORT_DUE_LATEST = "due_latest";

export const ACTION_ITEM_SORT_OPTIONS = [
  { value: ACTION_ITEM_SORT_DEFAULT, label: "Event date — recent first" },
  { value: ACTION_ITEM_SORT_SOURCE_OLDEST, label: "Event date — old first" },
  { value: ACTION_ITEM_SORT_DUE_SOONEST, label: "Due date — recent first" },
  { value: ACTION_ITEM_SORT_DUE_LATEST, label: "Due date — old first" },
  { value: ACTION_ITEM_SORT_ACTION_ASC, label: "Title A → Z" },
  { value: ACTION_ITEM_SORT_ACTION_DESC, label: "Title Z → A" },
  { value: ACTION_ITEM_SORT_CATEGORY_ASC, label: "Category A → Z" },
  { value: ACTION_ITEM_SORT_CATEGORY_DESC, label: "Category Z → A" },
];

/**
 * Meeting/capture timestamp for event-date list sorting.
 *
 * @example
 * getActionItemSourceTimeMs({ meeting_from_label: "13-04-2026 03:51 AM" });
 * // UTC epoch ms for that label
 */
export const getActionItemSourceTimeMs = (item) => {
  if (isManualActionItem(item)) {
    return parseActionItemCreatedAtMs(item);
  }
  return parseMeetingLabel(String(item?.meeting_from_label || "").trim());
};

/**
 * Resolve the effective list sort key (default → event date, recent first).
 *
 * @example
 * getEffectiveActionItemSortOrder("default");
 * // "source_newest"
 */
export const getEffectiveActionItemSortOrder = (sortOrder) =>
  sortOrder === ACTION_ITEM_SORT_DEFAULT ? ACTION_ITEM_SORT_SOURCE_NEWEST : sortOrder;

/**
 * Sort action items for the active list view order.
 *
 * Starred tasks are not moved: they sort like any other task under the active
 * ``sortOrder`` (event date, due date, title, category).
 *
 * @example
 * sortActionItemsForView(
 *   [
 *     { action_title: "Old", pinned: true, meeting_from_label: "01-01-2025 10:00 AM" },
 *     { action_title: "New", pinned: false, meeting_from_label: "01-01-2026 10:00 AM" },
 *   ],
 *   "default"
 * );
 * // [{ action_title: "New", ... }, { action_title: "Old", ... }]
 */
export const sortActionItemsForView = (list, sortOrder) => {
  const sorted = [...(Array.isArray(list) ? list : [])];
  const effectiveOrder = getEffectiveActionItemSortOrder(sortOrder);
  if (
    effectiveOrder === ACTION_ITEM_SORT_ACTION_ASC ||
    effectiveOrder === ACTION_ITEM_SORT_ACTION_DESC
  ) {
    return sorted.sort((a, b) => {
      const textA = String(a.action_title || a.action_item || "").trim();
      const textB = String(b.action_title || b.action_item || "").trim();
      const diff = textA.localeCompare(textB, undefined, { sensitivity: "base" });
      if (diff !== 0) {
        return effectiveOrder === ACTION_ITEM_SORT_ACTION_ASC ? diff : -diff;
      }
      return compareActionItemsStable(a, b);
    });
  }
  if (
    effectiveOrder === ACTION_ITEM_SORT_CATEGORY_ASC ||
    effectiveOrder === ACTION_ITEM_SORT_CATEGORY_DESC
  ) {
    return sorted.sort((a, b) => {
      const categoryA = getActionItemCategory(a);
      const categoryB = getActionItemCategory(b);
      const diff = categoryA.localeCompare(categoryB, undefined, { sensitivity: "base" });
      if (diff !== 0) {
        return effectiveOrder === ACTION_ITEM_SORT_CATEGORY_ASC ? diff : -diff;
      }
      return compareActionItemsStable(a, b);
    });
  }
  if (
    effectiveOrder === ACTION_ITEM_SORT_SOURCE_NEWEST ||
    effectiveOrder === ACTION_ITEM_SORT_SOURCE_OLDEST
  ) {
    return sorted.sort((a, b) => {
      const aMs = getActionItemSourceTimeMs(a);
      const bMs = getActionItemSourceTimeMs(b);
      if (!aMs && !bMs) return compareActionItemsStable(a, b);
      if (!aMs) return 1;
      if (!bMs) return -1;
      const diff = aMs - bMs;
      if (diff === 0) return compareActionItemsStable(a, b);
      return effectiveOrder === ACTION_ITEM_SORT_SOURCE_NEWEST ? -diff : diff;
    });
  }
  if (
    effectiveOrder === ACTION_ITEM_SORT_DUE_SOONEST ||
    effectiveOrder === ACTION_ITEM_SORT_DUE_LATEST
  ) {
    const direction =
      effectiveOrder === ACTION_ITEM_SORT_DUE_SOONEST ? "soonest" : "latest";
    return sorted.sort((a, b) => {
      const cmp = compareActionItemsByDueDate(a, b, { direction });
      if (cmp !== 0) return cmp;
      return compareActionItemsDefaultSortTieBreak(a, b);
    });
  }
  return sortActionItemsForView(sorted, ACTION_ITEM_SORT_SOURCE_NEWEST);
};

/**
 * Insert one item into a list using the active view sort order.
 *
 * @example
 * insertActionItemInViewSort(
 *   [{ due_date: "2026-06-01" }],
 *   { due_date: "2026-05-20", file_id: "manual::1" },
 *   "default"
 * );
 * // [{ due_date: "2026-05-20", ... }, { due_date: "2026-06-01", ... }]
 */
export const insertActionItemInViewSort = (list, item, sortOrder) =>
  sortActionItemsForView([...(Array.isArray(list) ? list : []), item], sortOrder);

/**
 * Return 'high', 'medium', or 'low' priority for an action item.
 *
 * High: explicit urgency keyword OR due within 2 days.
 * Medium: due within 6 days.
 * Low: everything else — no border shown.
 *
 * @example
 * computeActionItemPriority({ action_item: "Send recap ASAP", due_date: "" });
 * // "high"
 *
 * @example
 * computeActionItemPriority({ action_item: "Review contract", due_date: "2026-05-23" });
 * // "high"
 *
 * @example
 * computeActionItemPriority({ action_item: "Share template email", due_date: "" });
 * // "low"
 */
export const computeActionItemPriority = (item) => {
  const text = String(item?.action_item || "").toLowerCase();
  const dueDateStr = String(item?.due_date || "").trim();

  if (_HIGH_URGENCY_KEYWORDS.some((kw) => text.includes(kw))) return "high";

  if (dueDateStr) {
    try {
      const due = parseActionItemDueDate(dueDateStr);
      if (!due) return "low";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysUntil = Math.round((due - today) / 86400000);
      if (daysUntil <= 2) return "high";
      if (daysUntil <= 6) return "medium";
    } catch (_) {}
  }

  return "low";
};

const ACTION_ITEM_PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

/**
 * Deterministic ordering for merged action-item rows so silent refresh does not shuffle the UI.
 *
 * @example
 * compareActionItemsStable(
 *   { workflow_id: "b", file_id: "manual::1", key: "k", action_item: "Z" },
 *   { workflow_id: "a", file_id: "msg-1", key: "k", action_item: "A" }
 * );
 * // negative number (manual entry sorts before digest item)
 */
export const compareActionItemsStable = (a, b) => {
  const aManual = isManualActionItem(a) ? 0 : 1;
  const bManual = isManualActionItem(b) ? 0 : 1;
  if (aManual !== bManual) return aManual - bManual;
  if (aManual === 0) {
    const createdDiff = parseActionItemCreatedAtMs(b) - parseActionItemCreatedAtMs(a);
    if (createdDiff !== 0) return createdDiff;
  }
  const w = String(a.workflow_id || "").localeCompare(String(b.workflow_id || ""));
  if (w !== 0) return w;
  const f = String(a.file_id || "").localeCompare(String(b.file_id || ""));
  if (f !== 0) return f;
  const k = String(a.key || "").localeCompare(String(b.key || ""));
  if (k !== 0) return k;
  return String(a.action_item || "").localeCompare(String(b.action_item || ""));
};

/**
 * Tie-breaker for default webapp/extension list order after due-date comparison.
 * Undated rows: oldest `created_at` first (new tasks at the bottom).
 * Same due date: higher priority first, then `created_at` ascending.
 *
 * @example
 * compareActionItemsDefaultSortTieBreak(
 *   { due_date: "", created_at: "2026-06-01T10:00:00Z" },
 *   { due_date: "", created_at: "2026-06-02T10:00:00Z" }
 * );
 * // negative (Jun 1 before Jun 2)
 */
export const compareActionItemsDefaultSortTieBreak = (a, b) => {
  const aDue = parseActionItemDueDate(a?.due_date);
  const bDue = parseActionItemDueDate(b?.due_date);

  if (!aDue && !bDue) {
    const createdDiff = parseActionItemCreatedAtMs(a) - parseActionItemCreatedAtMs(b);
    if (createdDiff !== 0) return createdDiff;
  } else if (aDue && bDue && aDue.getTime() === bDue.getTime()) {
    const aRank = ACTION_ITEM_PRIORITY_RANK[computeActionItemPriority(a)] ?? 2;
    const bRank = ACTION_ITEM_PRIORITY_RANK[computeActionItemPriority(b)] ?? 2;
    if (aRank !== bRank) return aRank - bRank;
    const createdDiff = parseActionItemCreatedAtMs(a) - parseActionItemCreatedAtMs(b);
    if (createdDiff !== 0) return createdDiff;
  }

  const w = String(a.workflow_id || "").localeCompare(String(b.workflow_id || ""));
  if (w !== 0) return w;
  const f = String(a.file_id || "").localeCompare(String(b.file_id || ""));
  if (f !== 0) return f;
  const k = String(a.key || "").localeCompare(String(b.key || ""));
  if (k !== 0) return k;
  return String(a.action_item || "").localeCompare(String(b.action_item || ""));
};

/**
 * True when the item's due_date is in the past (before today).
 *
 * @example
 * isOverdue({ due_date: "2026-01-01" }); // true (if today > 2026-01-01)
 */
export const isOverdue = (item) => {
  const due = parseActionItemDueDate(item?.due_date);
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

/**
 * True when the item's due_date falls within the next 7 days (today through today+6).
 *
 * @example
 * isDueThisWeek({ due_date: "2026-05-28" }); // true if today is 2026-05-26
 */
export const isDueThisWeek = (item) => {
  const due = parseActionItemDueDate(item?.due_date);
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);
  return due >= today && due < weekEnd;
};

const DUE_DATE_MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Ordinal suffix for a calendar day (1 → "1st", 6 → "6th").
 *
 * @example
 * formatOrdinalDay(6); // "6th"
 * @example
 * formatOrdinalDay(23); // "23rd"
 */
const formatOrdinalDay = (day) => {
  const n = Number(day);
  if (!Number.isFinite(n) || n < 1) return String(day);
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

/**
 * Format a created-on date for action-item provenance (weekday, short month, year).
 *
 * @example
 * formatCreatedOnDate(Date.parse("2020-03-15T12:00:00Z"));
 * // "Sun, Mar 15, 2020"
 */
export const formatCreatedOnDate = (epochMs) => {
  if (!epochMs) return "";
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return "";
  const weekday = SOURCE_WEEKDAY_NAMES[date.getDay()];
  const month = SOURCE_MONTH_NAMES[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return `${weekday}, ${month} ${day}, ${year}`;
};

/**
 * Format a source-event date with an ordinal day (meeting / capture time).
 *
 * @example
 * formatSourceEventOrdinalDate(Date.parse("2020-02-13T12:00:00Z"));
 * // "Feb 13th" when the browser's current year is 2020
 *
 * @example
 * formatSourceEventOrdinalDate(Date.parse("2019-02-13T12:00:00Z"));
 * // "Feb 13th, 2019" when the browser's current year is 2020
 */
export const formatSourceEventOrdinalDate = (epochMs) => {
  if (!epochMs) return "";
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return "";
  const month = SOURCE_MONTH_NAMES[date.getMonth()];
  const dayOrdinal = formatOrdinalDay(date.getDate());
  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();
  if (year === currentYear) return `${month} ${dayOrdinal}`;
  return `${month} ${dayOrdinal}, ${year}`;
};

const WORKFLOW_TYPE_SOURCE_PHRASE = {
  zoom_daily_digest: "Zoom meeting",
  gong_daily_digest: "Gong call",
  email_daily_digest: "Gmail email",
  granola_daily_digest: "Granola note",
  notion_daily_digest: "Notion page",
  google_calendar_daily_digest: "Google Calendar event",
  google_meet_daily_digest: "Google Meet meeting",
};

/**
 * Repair common UTF-8 mojibake (e.g. em-dash stored as â).
 *
 * @example
 * repairMojibakeText("Experiments " + String.fromCharCode(0xe2, 0x80, 0x94) + " leakage");
 * // "Experiments — leakage"
 */
export const repairMojibakeText = (value) => {
  const text = String(value || "");
  if (!text) return text;
  let hasHigh = false;
  for (let i = 0; i < text.length; i += 1) {
    if (text.charCodeAt(i) >= 0x80) {
      hasHigh = true;
      break;
    }
  }
  if (!hasHigh) return text;
  try {
    // escape() treats code units as Latin-1; decodeURIComponent reads UTF-8.
    // eslint-disable-next-line no-restricted-globals, no-undef
    const repaired = decodeURIComponent(escape(text));
    return repaired || text;
  } catch (_) {
    return text;
  }
};

const MEETING_SUBJECT_FIELDS = [
  "meeting_subject",
  "meeting_name",
  "meeting_title",
  "source_subject",
  "subject",
  "source_meeting_name",
  "title",
  "thread_subject",
];

/**
 * Resolve a human-readable meeting / source title when the API provides one.
 *
 * Prefers explicit meeting_* aliases, then falls back to the action item's
 * `title` (Gmail email subject or meeting topic), then legacy `thread_subject`
 * from message-shape payloads that still use that key.
 *
 * @example
 * resolveActionItemMeetingSubject({ meeting_subject: "Customer foo QBR" });
 * // "Customer foo QBR"
 *
 * @example
 * resolveActionItemMeetingSubject({ title: "Re: API rate limits" });
 * // "Re: API rate limits"
 */
export const resolveActionItemMeetingSubject = (item) => {
  if (!item || typeof item !== "object") return "";
  for (const field of MEETING_SUBJECT_FIELDS) {
    const value = repairMojibakeText(String(item[field] || "").trim());
    if (value) return value;
  }
  return "";
};

/**
 * Short phrase for where an action item came from (Zoom meeting, page selection, etc.).
 *
 * @example
 * resolveActionItemSourcePhrase(
 *   { workflow_id: "wf-1", meeting_from_label: "13-02-2020 10:00 AM" },
 *   { workflowTypeById: new Map([["wf-1", "zoom_daily_digest"]]) }
 * );
 * // "Zoom meeting"
 *
 * @example
 * resolveActionItemSourcePhrase({ file_id: "manual::1", source_app: "Cursor" });
 * // "your note - Cursor"
 */
export const resolveActionItemSourcePhrase = (item, { workflowTypeById } = {}) => {
  if (!item || typeof item !== "object") return "";
  if (isManualActionItem(item)) {
    const pageUrl = resolveActionItemPageUrl(item);
    if (pageUrl) {
      try {
        const host = new URL(pageUrl).hostname.replace(/^www\./i, "");
        return host ? `page on ${host}` : "page selection";
      } catch (_) {
        return "page selection";
      }
    }
    const sourceApp = String(item.source_app || "").trim();
    return sourceApp ? `your note - ${sourceApp}` : "your note";
  }
  if (isCaptureWorkflowItem(item)) return "Captured";

  const workflowId = String(item.workflow_id || "").trim();
  const workflowType =
    String(item.workflow_type || "").trim() ||
    String(workflowTypeById?.get(workflowId) || "").trim();
  if (workflowType && WORKFLOW_TYPE_SOURCE_PHRASE[workflowType]) {
    return WORKFLOW_TYPE_SOURCE_PHRASE[workflowType];
  }

  const workflowName = String(item.workflow_name || "").trim().toLowerCase();
  if (workflowName.includes("zoom")) return "Zoom meeting";
  if (workflowName.includes("gong")) return "Gong call";
  if (workflowName.includes("gmail") || workflowName.includes("email")) return "Gmail email";
  if (workflowName.includes("granola")) return "Granola note";
  if (workflowName.includes("notion")) return "Notion page";

  return "meeting";
};

/**
 * Sentence parts for the action-item source line (Created on … from … on …).
 *
 * @example
 * buildActionItemSourceProvenanceParts(
 *   {
 *     created_at: "2020-03-15T12:00:00Z",
 *     meeting_from_label: "13-02-2020 10:00 AM",
 *     meeting_subject: "customer foo",
 *     workflow_id: "wf-1",
 *   },
 *   { workflowTypeById: new Map([["wf-1", "zoom_daily_digest"]]) }
 * );
 * // {
 * //   createdLabel: "Sun, Mar 15, 2020",
 * //   fromClause: "Zoom meeting with customer foo",
 * //   eventDateLabel: "Feb 13th",
 * //   title: "Created on Sun, Mar 15, 2020 from Zoom meeting with customer foo on Feb 13th",
 * // }
 */
export const buildActionItemSourceProvenanceParts = (item, { workflowTypeById } = {}) => {
  if (!item || typeof item !== "object") return null;

  const isManual = isManualActionItem(item);
  const createdMs = parseActionItemCreatedAtMs(item);
  const meetingMs = parseMeetingLabel(String(item.meeting_from_label || "").trim());
  const createdOnMs = createdMs || (!isManual ? meetingMs : 0);
  const createdLabel = createdOnMs ? formatCreatedOnDate(createdOnMs) : "";

  const sourcePhrase = resolveActionItemSourcePhrase(item, { workflowTypeById });
  const subject = resolveActionItemMeetingSubject(item);
  let fromClause = "";
  if (sourcePhrase) {
    fromClause = subject ? `${sourcePhrase} with ${subject}` : sourcePhrase;
  }

  const eventDateLabel = !isManual && meetingMs ? formatSourceEventOrdinalDate(meetingMs) : "";
  if (!createdLabel && !fromClause && !eventDateLabel) return null;

  const titleParts = [];
  if (createdLabel) titleParts.push(`Created on ${createdLabel}`);
  if (fromClause) titleParts.push(`from ${fromClause}`);
  if (eventDateLabel) titleParts.push(`on ${eventDateLabel}`);

  return {
    createdLabel,
    fromClause,
    eventDateLabel,
    title: titleParts.join(" "),
  };
};

/**
 * Compact date of the meeting / email / page a task came from, for the list row.
 * Empty for manual tasks and for rows with no parseable event time, so a row never
 * shows a wrong date (never falls back to the sync / created date).
 *
 * @example
 * resolveActionItemEventDateShort({ meeting_from_label: "13-02-2020 10:00 AM" });
 * // "Feb 13, 2020" (year omitted when it is the current year)
 * @example
 * resolveActionItemEventDateShort({ file_id: "manual::1", meeting_from_label: "13-02-2020 10:00 AM" });
 * // ""
 */
export const resolveActionItemEventDateShort = (item) => {
  if (!item || typeof item !== "object" || isManualActionItem(item)) return "";
  return formatMeetingLabelShort(String(item.meeting_from_label || "").trim());
};

/**
 * Format a due_date string for the due-date chip (weekday, ordinal day, full month).
 *
 * @example
 * formatDueDate("2026-06-06"); // "Sat, 6th June" when parsed in local timezone
 * @example
 * formatDueDate("2025-06-06"); // "Fri, 6th June 2025" when current year is 2026
 * @example
 * formatDueDate(""); // ""
 */
export const formatDueDate = (dueDateStr) => {
  const due = parseActionItemDueDate(dueDateStr);
  if (!due) return "";
  const weekday = SOURCE_WEEKDAY_NAMES[due.getDay()];
  const month = DUE_DATE_MONTH_NAMES[due.getMonth()];
  const dayOrdinal = formatOrdinalDay(due.getDate());
  const year = due.getFullYear();
  const currentYear = new Date().getFullYear();
  if (year === currentYear) {
    return `${weekday}, ${dayOrdinal} ${month}`;
  }
  return `${weekday}, ${dayOrdinal} ${month} ${year}`;
};

/** Selections at or below this length skip the LLM and use local title/body synthesis. */
export const LOCAL_SELECTION_MAX_LENGTH = 200;

export const buildLocalActionItemFromSelection = (selectionText, pageUrl = "") => {
  const snippet = String(selectionText || "").replace(/\s+/g, " ").trim().slice(0, 4000);
  if (!snippet) {
    return { action_title: "View", action_item_text: "View" };
  }
  const action_title = buildCompactActionItemTitle(snippet);
  const body =
    snippet.length <= 280
      ? snippet
      : `${action_title}${ACTION_ITEM_CONTEXT_MARKER}${snippet}`;
  const action_item_text = appendSourcePageLineIfMissing(body, pageUrl);
  return { action_title, action_item_text };
};

/**
 * Filter items whose event date (meeting_from_label or created_at for manual items)
 * falls within the given date range. Items with no parseable event date pass through.
 *
 * @example
 * filterItemsByEventDateRange(items, "2026-06-01", "2026-06-25");
 * // only items with event date on or between Jun 1 and Jun 25
 */
export const filterItemsByEventDateRange = (list, from, to) => {
  const fromMs = from ? new Date(from + "T00:00:00").getTime() : 0;
  const toMs = to ? new Date(to + "T23:59:59").getTime() : 0;
  if (!fromMs && !toMs) return list;
  return (Array.isArray(list) ? list : []).filter((item) => {
    const ms = getActionItemSourceTimeMs(item);
    if (!ms) return true;
    if (fromMs && ms < fromMs) return false;
    if (toMs && ms > toMs) return false;
    return true;
  });
};

/**
 * Filter items down to aging / auto-archived items when the Aging chip is active.
 *
 * Pending warn-window rows use ``is_aging``; system-archived rows use
 * ``auto_archived`` so the same filter works in To do and Archived.
 *
 * @example
 * filterItemsByAgingFlag([{ is_aging: true }, { is_aging: false }], true)
 * // => [{ is_aging: true }]
 *
 * @example
 * filterItemsByAgingFlag([{ auto_archived: true, status: "ignore" }], true)
 * // => [{ auto_archived: true, status: "ignore" }]
 *
 * @example
 * filterItemsByAgingFlag([{ is_aging: true }], false)
 * // => [{ is_aging: true }]  (no-op when filter inactive)
 */
export function filterItemsByAgingFlag(items, isAgingFilterActive) {
  const list = Array.isArray(items) ? items : [];
  if (!isAgingFilterActive) return list;
  return list.filter(
    (item) => Boolean(item?.is_aging) || Boolean(item?.auto_archived)
  );
}

/**
 * Days since last activity (`updated_at`, falling back to `created_at`) for quiet badge text.
 *
 * @example
 * computeActionItemQuietDays({ updated_at: "2026-06-01T00:00:00Z" }, Date.parse("2026-07-08T00:00:00Z"));
 * // 37
 */
export const computeActionItemQuietDays = (item, nowMs = Date.now()) => {
  const updatedMs = Date.parse(String(item?.updated_at || "").trim());
  const createdMs = Date.parse(String(item?.created_at || "").trim());
  const referenceMs = Number.isFinite(updatedMs)
    ? updatedMs
    : Number.isFinite(createdMs)
      ? createdMs
      : 0;
  if (!referenceMs || !Number.isFinite(nowMs)) return 0;
  return Math.max(0, Math.floor((nowMs - referenceMs) / 86400000));
};

/**
 * Label for the aging meta badge on a quiet pending row.
 *
 * @example
 * formatActionItemQuietBadgeLabel(37);
 * // "Quiet for 37 days"
 */
export const formatActionItemQuietBadgeLabel = (quietDays) => {
  const days = Math.max(0, Number(quietDays) || 0);
  if (days <= 0) return "Quiet";
  return `Quiet for ${days} day${days === 1 ? "" : "s"}`;
};

/**
 * Stable composite key for an action item row — safe to use as a React key or Map key.
 *
 * Prefers the server-assigned `item_id`, which never changes for the life of the row.
 * `key` is a hash of the note text (see action_item_key on the backend), so falling back
 * to the workflow/key/file_id composite is only safe for rows that don't have an item_id
 * yet — e.g. an optimistic manual-add placeholder before the create call resolves. Once a
 * row has a real item_id, editing its note text must not change its identity: that's what
 * let a background refresh see the same row as "new" mid-edit and duplicate it.
 *
 * @example
 * getItemStableId({ item_id: "uuid-abc", workflow_id: "wf-1", key: "abc", file_id: "f-1" });
 * // "uuid-abc"
 * @example
 * getItemStableId({ workflow_id: "wf-1", key: "abc", file_id: "f-1" });
 * // "wf-1::abc::f-1"
 */
export const getItemStableId = (item) => {
  const itemId = String(item?.item_id || "").trim();
  if (itemId) return itemId;
  return [
    String(item?.workflow_id || "").trim(),
    String(item?.key || "").trim(),
    String(item?.file_id || "").trim(),
  ]
    .filter(Boolean)
    .join("::");
};

/**
 * Identity to key the details sidebar (and its local edit state) on. Prefers a manually
 * added item's client-generated id, which stays constant across the optimistic-item swap
 * when the server assigns its real key/file_id — so an in-progress note edit isn't reset
 * by that swap. Falls back to the regular stable id for everything else.
 *
 * @example
 * getItemSidebarKey({ workflow_id: "wf-1", key: "", file_id: "manual::pending-1", _clientKey: "manual::pending-1" });
 * // "manual::pending-1"
 */
export const getItemSidebarKey = (item) =>
  String(item?._clientKey || "").trim() || getItemStableId(item);
